import React, { useState, useEffect, useCallback } from "react";
import StrategyEngineService from "../services/StrategyEngineService";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Divider,
  FormControlLabel,
  Switch,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Slider,
  Tooltip,
  ListSubheader,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import ChatIcon from "@mui/icons-material/Chat";
import RedditIcon from "@mui/icons-material/Reddit";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import BarChartIcon from "@mui/icons-material/BarChart";
import PagesIcon from "@mui/icons-material/Pages";
import CurrencyBitcoinIcon from "@mui/icons-material/CurrencyBitcoin";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import TokenIcon from "@mui/icons-material/Token";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SearchIcon from "@mui/icons-material/Search";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import SecurityIcon from "@mui/icons-material/Security";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import DescriptionIcon from "@mui/icons-material/Description";
import StarIcon from "@mui/icons-material/Star";
import SyncIcon from "@mui/icons-material/Sync";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import LockIcon from "@mui/icons-material/Lock";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LinkIcon from "@mui/icons-material/Link";
import TuneIcon from "@mui/icons-material/Tune";
import { BUILT_IN_MODES, SETTING_ACTIONS, ACTION_CATEGORIES, applyModeAndActions, getOperationalActions, buildConfigurationOrder } from "../config/tradingPresets";
import { ModePresetSelector, QuickControls, AdvancedAccordions } from "./scheduler";

// Smart Symbol Universes
const SYMBOL_UNIVERSES = [
  {
    id: "majors",
    name: "Major Cryptocurrencies",
    symbols: ["BTC/USDT", "ETH/USDT", "BNB/USDT", "SOL/USDT", "XRP/USDT"],
  },
  {
    id: "defi",
    name: "DeFi Tokens",
    symbols: ["UNI/USDT", "AAVE/USDT", "LINK/USDT", "MKR/USDT", "CRV/USDT"],
  },
  {
    id: "top10",
    name: "Top 10 Cryptocurrencies",
    symbols: [
      "BTC/USDT",
      "ETH/USDT",
      "BNB/USDT",
      "SOL/USDT",
      "XRP/USDT",
      "ADA/USDT",
      "AVAX/USDT",
      "DOT/USDT",
      "MATIC/USDT",
      "LINK/USDT",
    ],
  },
  { id: "custom", name: "Custom Universe", symbols: [] },
];

// Available Brokers for Smart Symbol (all supported exchanges)
const AVAILABLE_BROKERS = [
  // Crypto Exchanges
  { id: "binance", name: "Binance", fees: "0.1%", type: "crypto" },
  { id: "kraken", name: "Kraken", fees: "0.16-0.26%", type: "crypto" },
  { id: "coinbase", name: "Coinbase", fees: "0.4-0.6%", type: "crypto" },
  { id: "bybit", name: "Bybit", fees: "0.1%", type: "crypto" },
  { id: "okx", name: "OKX", fees: "0.08-0.1%", type: "crypto" },
  { id: "kucoin", name: "KuCoin", fees: "0.1%", type: "crypto" },
  { id: "gate", name: "Gate.io", fees: "0.2%", type: "crypto" },
  { id: "bitfinex", name: "Bitfinex", fees: "0.1-0.2%", type: "crypto" },
  { id: "huobi", name: "HTX (Huobi)", fees: "0.2%", type: "crypto" },
  { id: "mexc", name: "MEXC", fees: "0.2%", type: "crypto" },
  // Stock Brokers
  {
    id: "interactive-brokers",
    name: "Interactive Brokers",
    fees: "$0.005/share",
    type: "stock",
  },
  { id: "alpaca", name: "Alpaca", fees: "Commission-free", type: "stock" },
  {
    id: "schwab",
    name: "Charles Schwab",
    fees: "Commission-free",
    type: "stock",
  },
  { id: "etrade", name: "E*TRADE", fees: "Commission-free", type: "stock" },
  { id: "tradier", name: "Tradier", fees: "$0.35/contract", type: "stock" },
];

// AI models available for selection
const AI_MODELS = [
  {
    id: "openai-gpt-4",
    name: "OpenAI GPT-4",
    provider: "openai",
    assistantSupport: true,
  },
  {
    id: "openai-gpt-4-turbo",
    name: "OpenAI GPT-4 Turbo",
    provider: "openai",
    assistantSupport: true,
  },
  {
    id: "openai-gpt-3.5-turbo",
    name: "OpenAI GPT-3.5 Turbo",
    provider: "openai",
    assistantSupport: true,
  },
  {
    id: "anthropic-claude-3-opus",
    name: "Anthropic Claude 3 Opus",
    provider: "anthropic",
    assistantSupport: false,
  },
  {
    id: "anthropic-claude-3-sonnet",
    name: "Anthropic Claude 3 Sonnet",
    provider: "anthropic",
    assistantSupport: false,
  },
  {
    id: "anthropic-claude-3-haiku",
    name: "Anthropic Claude 3 Haiku",
    provider: "anthropic",
    assistantSupport: false,
  },
  {
    id: "google-gemini-1.5-pro",
    name: "Google Gemini 1.5 Pro",
    provider: "google",
    assistantSupport: false,
  },
  {
    id: "google-gemini-1.5",
    name: "Google Gemini 1.5",
    provider: "google",
    assistantSupport: false,
  },
  {
    id: "mistral-large",
    name: "Mistral Large",
    provider: "mistral",
    assistantSupport: false,
  },
  {
    id: "mistral-medium",
    name: "Mistral Medium",
    provider: "mistral",
    assistantSupport: false,
  },
  {
    id: "mistral-small",
    name: "Mistral Small",
    provider: "mistral",
    assistantSupport: false,
  },
  {
    id: "llama3-70b",
    name: "Llama 3 70B",
    provider: "meta",
    assistantSupport: false,
  },
  {
    id: "llama3-8b",
    name: "Llama 3 8B",
    provider: "meta",
    assistantSupport: false,
  },
];

// Social Media APIs for fundamental analysis
const SOCIAL_MEDIA_APIS = [
  {
    id: "twitter",
    name: "Twitter/X",
    icon: "TwitterIcon",
    description: "Access to Twitter/X feed and trends",
  },
  {
    id: "reddit",
    name: "Reddit",
    icon: "RedditIcon",
    description: "Sentiment from relevant subreddits",
  },
  {
    id: "stocktwits",
    name: "StockTwits",
    icon: "ChatIcon",
    description: "Stock-specific social sentiment",
  },
  {
    id: "linkedin",
    name: "LinkedIn",
    icon: "LinkedInIcon",
    description: "Company news and updates",
  },
  {
    id: "youtube",
    name: "YouTube",
    icon: "YouTubeIcon",
    description: "Financial channels and news",
  },
];

// Market Data APIs for fundamental analysis
const MARKET_DATA_APIS = [
  {
    id: "alphavantage",
    name: "Alpha Vantage",
    description: "Fundamental data, earnings, financials",
  },
  {
    id: "yahoofinance",
    name: "Yahoo Finance",
    description: "Market news, financials, analyst ratings",
  },
  {
    id: "bloomberg",
    name: "Bloomberg",
    description: "Premium financial data and news",
  },
  { id: "newsapi", name: "News API", description: "Aggregated financial news" },
  {
    id: "financeapi",
    name: "Financial Modeling Prep",
    description: "Financial statements and ratios",
  },
  {
    id: "sec-edgar",
    name: "SEC EDGAR",
    description: "Regulatory filings and disclosures",
  },
];

// Crypto Analytics APIs for fundamental analysis
const CRYPTO_ANALYTICS_APIS = [
  {
    id: "whaleAlert",
    name: "Whale Alert",
    icon: "CurrencyBitcoinIcon",
    description: "Track large cryptocurrency transactions",
    website: "https://whale-alert.io",
  },
  {
    id: "glassnode",
    name: "Glassnode",
    icon: "TimelineIcon",
    description: "On-chain market intelligence",
    website: "https://glassnode.com",
  },
  {
    id: "santiment",
    name: "Santiment",
    icon: "ShowChartIcon",
    description: "Crypto-specific data feeds and analytics",
    website: "https://api.santiment.net",
  },
  {
    id: "nansen",
    name: "Nansen",
    icon: "DataUsageIcon",
    description: "Blockchain analytics platform",
    website: "https://www.nansen.ai",
  },
  {
    id: "arkhamIntelligence",
    name: "Arkham Intelligence",
    icon: "MonetizationOnIcon",
    description: "Blockchain intelligence for wallets and entities",
    website: "https://www.arkhamintelligence.com",
  },
  {
    id: "cryptoQuant",
    name: "CryptoQuant",
    icon: "BarChartIcon",
    description: "On-chain data analytics platform",
    website: "https://cryptoquant.com",
  },
  {
    id: "intoTheBlock",
    name: "IntoTheBlock",
    icon: "TokenIcon",
    description: "Crypto market intelligence",
    website: "https://app.intotheblock.com",
  },
  {
    id: "coinbaseCloud",
    name: "Coinbase Cloud (TokenAnalyst)",
    icon: "CurrencyBitcoinIcon",
    description: "Blockchain data infrastructure",
    website: "https://www.coinbase.com/cloud",
  },
  {
    id: "duneAnalytics",
    name: "Dune Analytics",
    icon: "DataUsageIcon",
    description: "SQL-based crypto analytics platform",
    website: "https://dune.com",
  },
  {
    id: "etherscan",
    name: "Etherscan",
    icon: "TimelineIcon",
    description: "Ethereum blockchain explorer and analytics",
    website: "https://etherscan.io/apis",
  },
];

// Default prompt templates for each node type
const DEFAULT_PROMPTS = {
  technicalAnalyst: `You are a Technical Analysis expert specializing in financial charts and indicators.

Task: Analyze the given market data to identify trading opportunities based on technical indicators and chart patterns.

Return your analysis with clear signal direction (BUY/SELL/HOLD), confidence level (1-10), and a brief explanation of the reasoning. Focus on ${"{symbol}"} with timeframe ${"{timeframe}"}.`,

  fundamentalAnalyst: `You are a Fundamental Analysis expert specializing in news and market sentiment.

Task: Analyze the latest news, financial reports, and market sentiment to determine potential market impacts.

Return your analysis with signal direction (BULLISH/BEARISH/NEUTRAL), confidence level (1-10), and a brief explanation of key factors. Focus on topics: ${"{topics}"}.`,

  riskManager: `You are a Risk Management expert specializing in position sizing and risk control.

Task: Evaluate trading signals and determine appropriate position sizes while managing overall portfolio risk.

Return specific trade parameters including position size, stop loss, take profit, and risk percentage. Maximum risk per trade: ${"{maxRisk}"}.`,

  tradeHistorian: `You are a Trading History expert specializing in pattern recognition across historical trades.

Task: Analyze past trading performance to identify successful patterns and areas for improvement.

Return insights on win rate, average profit/loss, best performing strategies, and recommendations for optimization.`,

  reportSynthesizer: `You are a Report Generation expert specializing in creating clear trading summaries.

Task: Synthesize all trading analysis and results into a comprehensive report.

Return a structured report with executive summary, detailed analysis sections, and clear recommendations for action.`,

  executionLayer: `You are a Trade Execution expert specializing in optimal order placement.

Task: Execute trading decisions efficiently while minimizing slippage and market impact.

Return execution details including fill price, execution time, and any relevant market conditions observed.`,

  dataSource: `You are a Data Collection expert specializing in financial market information.

Task: Gather and pre-process financial data from various sources to ensure high quality inputs for analysis.

Return clean, structured data with proper formatting and handling of missing values.`,

  condition: `You are a Conditional Logic expert specializing in trade rule execution.

Task: Evaluate if specific market conditions have been met for trade execution.

Return a boolean decision (TRUE/FALSE) with explanation of which conditions were met or failed.`,

  memory: `You are a Trading Memory expert specializing in contextual awareness of market history.

Task: Maintain a relevant history of market conditions and trading decisions to inform current analysis.

Return key historical context that is relevant to current market conditions.`,

  telegramReport: `You are a Reporting expert specializing in concise trading updates.

Task: Generate clear, actionable trading reports suitable for messaging platforms.

Return a concise message with key information formatted appropriately for Telegram.`,

  scheduler: `You are a Scheduling coordinator for automated trading strategies.

Task: Manage the timing and execution of trading signals based on confidence thresholds.

Filter signals that meet the confidence threshold and prepare them for automated execution.`,
};

function NodeSettingsPanel({ node, onSettingsChange, onClose, strategyId }) {
  // State variables
  const [settings, setSettings] = useState(node?.data?.settings || {});
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [selectedModel, setSelectedModel] = useState(
    settings?.aiModel || "openai-gpt-4",
  );
  const [customPrompt, setCustomPrompt] = useState(
    settings?.customPrompt || DEFAULT_PROMPTS[node?.type] || "",
  );
  const [useAssistant, setUseAssistant] = useState(
    settings?.useAssistant !== undefined ? settings?.useAssistant : true,
  );
  const [assistantId, setAssistantId] = useState(settings?.assistantId || "");
  const [testStatus, setTestStatus] = useState(null); // null, 'loading', 'success', 'error'
  const [testMessage, setTestMessage] = useState("");

  // State for social media, market data, and crypto analytics API connections
  const [socialMediaConnections, setSocialMediaConnections] = useState(
    settings?.socialMediaConnections || {},
  );
  const [marketDataConnections, setMarketDataConnections] = useState(
    settings?.marketDataConnections || {},
  );
  const [cryptoAnalyticsConnections, setCryptoAnalyticsConnections] = useState(
    settings?.cryptoAnalyticsConnections || {},
  );
  const [apiTestStatus, setApiTestStatus] = useState({});
  const [apiTestMessages, setApiTestMessages] = useState({});

  // Smart Symbol Discovery state
  const [smartSymbolEnabled, setSmartSymbolEnabled] = useState(
    settings?.smartSymbolEnabled || false,
  );
  const [selectedUniverse, setSelectedUniverse] = useState(
    settings?.symbolUniverse || "majors",
  );
  const [selectedBrokers, setSelectedBrokers] = useState(
    settings?.selectedBrokers || ["binance"],
  );
  const [customSymbols, setCustomSymbols] = useState(
    settings?.customSymbols || "",
  );
  const [minConfidenceThreshold, setMinConfidenceThreshold] = useState(
    settings?.minConfidenceThreshold || 60,
  );
  const [feeAwareSelection, setFeeAwareSelection] = useState(
    settings?.feeAwareSelection !== false,
  );
  const [smartSymbolResult, setSmartSymbolResult] = useState(null);
  const [smartSymbolLoading, setSmartSymbolLoading] = useState(false);

  // Trading Mode Preset state
  const [selectedModeId, setSelectedModeId] = useState(settings?.modeId || 'custom');
  const [selectedActions, setSelectedActions] = useState(settings?.activeActions || []);
  const [modeAppliedBanner, setModeAppliedBanner] = useState(null);

  // Next execution time state for scheduler
  const [nextExecutionInfo, setNextExecutionInfo] = useState(null);
  const [countdown, setCountdown] = useState(null);
  
  // Broker connection status for Scheduler Node
  const [brokerConnections, setBrokerConnections] = useState({});
  const [brokerConnectionsLoading, setBrokerConnectionsLoading] = useState(false);

  // Load broker connections from backend for Scheduler nodes
  useEffect(() => {
    if (node?.type === "scheduler") {
      const loadBrokerConnections = async () => {
        setBrokerConnectionsLoading(true);
        try {
          const authToken = localStorage.getItem('auth_token');
          const response = await fetch(`${process.env.REACT_APP_STRATEGY_ENGINE_URL || 'https://strategy-engine.houman-ghavamzadeh.workers.dev'}/api/connections`, {
            headers: {
              'Content-Type': 'application/json',
              ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
            },
          });
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              // Combine brokers and cryptoExchanges
              const allBrokers = {};
              [...(result.data.brokers || []), ...(result.data.cryptoExchanges || [])].forEach(broker => {
                allBrokers[broker.id] = {
                  status: broker.status,
                  lastConnected: broker.lastConnected,
                  tradingEnabled: broker.tradingEnabled,
                  nodeIntegrated: broker.nodeIntegrated,
                  paperTrading: broker.paperTrading,
                };
              });
              setBrokerConnections(allBrokers);
            }
          }
        } catch (error) {
          console.log("Could not load broker connections:", error);
        } finally {
          setBrokerConnectionsLoading(false);
        }
      };
      loadBrokerConnections();
    }
  }, [node?.type]);

  // Initialize API connection states with default values
  useEffect(() => {
    if (node?.type === "fundamentalAnalyst") {
      // Initialize social media connections if not already set
      if (Object.keys(socialMediaConnections).length === 0) {
        const initialSocialMediaConnections = {};
        SOCIAL_MEDIA_APIS.forEach((api) => {
          initialSocialMediaConnections[api.id] =
            settings?.socialMediaConnections?.[api.id] || false;
        });
        setSocialMediaConnections(initialSocialMediaConnections);
      }

      // Initialize market data connections if not already set
      if (Object.keys(marketDataConnections).length === 0) {
        const initialMarketDataConnections = {};
        MARKET_DATA_APIS.forEach((api) => {
          initialMarketDataConnections[api.id] =
            settings?.marketDataConnections?.[api.id] || false;
        });
        setMarketDataConnections(initialMarketDataConnections);
      }

      // Initialize crypto analytics connections if not already set
      if (Object.keys(cryptoAnalyticsConnections).length === 0) {
        const initialCryptoAnalyticsConnections = {};
        CRYPTO_ANALYTICS_APIS.forEach((api) => {
          initialCryptoAnalyticsConnections[api.id] =
            settings?.cryptoAnalyticsConnections?.[api.id] || false;
        });
        setCryptoAnalyticsConnections(initialCryptoAnalyticsConnections);
      }
    }
  }, [
    node?.type,
    settings,
    socialMediaConnections,
    marketDataConnections,
    cryptoAnalyticsConnections,
  ]);

  // Update settings when a DIFFERENT node is selected (not on every reference change)
  // v3.9.15: Changed dep from [node] to [node?.id] to prevent resetting unsaved
  // settings (e.g. AI Exit Mode dropdown) when React Flow creates a new node reference.
  useEffect(() => {
    if (node) {
      setSettings(node.data?.settings || {});
      setSelectedModel(node.data?.settings?.aiModel || "openai-gpt-4");
      setCustomPrompt(
        node.data?.settings?.customPrompt || DEFAULT_PROMPTS[node?.type] || "",
      );
      setUseAssistant(
        node.data?.settings?.useAssistant !== undefined
          ? node.data?.settings?.useAssistant
          : true,
      );
      setAssistantId(node.data?.settings?.assistantId || "");

      // Initialize social media, market data, and crypto analytics connections
      if (node.type === "fundamentalAnalyst") {
        setSocialMediaConnections(
          node.data?.settings?.socialMediaConnections || {},
        );
        setMarketDataConnections(
          node.data?.settings?.marketDataConnections || {},
        );
        setCryptoAnalyticsConnections(
          node.data?.settings?.cryptoAnalyticsConnections || {},
        );
      }
    }
  }, [node?.id]);

  // Fetch schedule info for scheduler nodes and update countdown
  // v4.4.9: Also merge KV schedule values into settings so the UI shows the latest
  // saved values instead of stale DB node params. This fixes settings reverting on reopen.
  useEffect(() => {
    const isSchedulerFamily = node?.type === "scheduler"
      || node?.type === "entryGate" || node?.type === "exitEngine"
      || node?.type === "rescueManager" || node?.type === "budgetAllocator"
      || node?.type === "decisionEngine";
    if (isSchedulerFamily && strategyId) {
      let merged = false; // Only merge once on mount, not on every 30s refresh
      const fetchScheduleInfo = async () => {
        try {
          const scheduleData = await StrategyEngineService.getSchedule(strategyId);
          if (scheduleData?.nextExecutionDisplay) {
            setNextExecutionInfo(scheduleData.nextExecutionDisplay);
          } else if (scheduleData?.nextRun) {
            // Fallback to basic nextRun info
            const nextRunDate = new Date(scheduleData.nextRun);
            const timezone = scheduleData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            const nextInTz = new Date(nextRunDate.toLocaleString('en-US', { timeZone: timezone }));
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            setNextExecutionInfo({
              time: `${String(nextInTz.getHours()).padStart(2,'0')}:${String(nextInTz.getMinutes()).padStart(2,'0')}`,
              day: dayNames[nextInTz.getDay()],
              timestamp: scheduleData.nextRun,
              mode: scheduleData.goldenHoursMode ? 'golden_hours' : 'standard'
            });
          }
          // v4.4.9: Merge KV schedule values into settings on first load.
          // KV is the source of truth for schedule params (the DB node data may be stale).
          if (!merged && scheduleData) {
            merged = true;
            const boolToYesNo = (val) => val === true ? "Yes" : val === false ? "No" : val;
            const kvOverrides = {};
            // Map KV schedule fields to settings field names
            const fieldMap = {
              scanInterval: 'scanInterval', manageInterval: 'manageInterval',
              broker: 'broker', tradingMode: 'tradingMode', leverage: 'leverage',
              leverageEntryMode: 'leverageEntryMode',
              confidenceThreshold: 'confidenceThreshold',
              autoExecute: 'autoExecute', confirmationMode: 'confirmationMode',
              maxSignalsPerRun: 'maxSignalsPerRun', paperTrading: 'paperTrading',
              positionSizeUsd: 'positionSizeUsd', positionSizePercent: 'positionSizePercent',
              exitStrategy: 'exitStrategy',
              stopLossPercent: 'stopLossPercent', takeProfitPercent: 'takeProfitPercent',
              useTrailingStop: 'useTrailingStop', trailingStopAiMode: 'trailingStopAiMode',
              trailingStopPercent: 'trailingStopPercent',
              cooldownMinutes: 'cooldownMinutes', cooldownPerSymbol: 'cooldownPerSymbol',
              retryOnFailure: 'retryOnFailure', maxRetries: 'maxRetries',
              retryDelaySeconds: 'retryDelaySeconds',
              notifyOnTrade: 'notifyOnTrade', notifyOnError: 'notifyOnError',
              telegramEnabled: 'telegramEnabled',
              moneyManagementEnabled: 'moneyManagementEnabled',
              maxConcurrentPositions: 'maxConcurrentPositions',
              allocationMode: 'allocationMode', reservePercent: 'reservePercent',
              maxPerTrade: 'maxPerTrade', maxPerSymbol: 'maxPerSymbol',
              rescueScaleInEnabled: 'rescueScaleInEnabled', maxRescueAdds: 'maxRescueAdds',
              rescueMode: 'rescueMode', maxRescueAttempts: 'maxRescueAttempts',
              rescueAddPremiumPct: 'rescueAddPremiumPct', rescueAddPremiumAI: 'rescueAddPremiumAI',
              rescueReservePercent: 'rescueReservePercent',
              enableVolumeControl: 'enableVolumeControl', volumeStrictness: 'volumeStrictness',
              strictEntryEnabled: 'strictEntryEnabled', enableCorrelation: 'enableCorrelation',
              sellFilterStrict: 'sellFilterStrict', enableMLGate: 'enableMLGate',
              trendFilter1m: 'trendFilter1m', trendFilter3m: 'trendFilter3m',
              trendFilter5m: 'trendFilter5m', trendFilter15m: 'trendFilter15m',
              trendFilter30m: 'trendFilter30m', trendFilter1h: 'trendFilter1h',
              trendFilter4h: 'trendFilter4h',
              lastCandleFilter1m: 'lastCandleFilter1m', lastCandleFilter3m: 'lastCandleFilter3m',
              lastCandleFilter5m: 'lastCandleFilter5m', lastCandleFilter15m: 'lastCandleFilter15m',
              lastCandleFilter30m: 'lastCandleFilter30m', lastCandleFilter1h: 'lastCandleFilter1h',
              lastCandleFilter4h: 'lastCandleFilter4h',
              indicatorStack: 'indicatorStack',
              skipOpenSymbols: 'skipOpenSymbols', smartSymbolEnabled: 'smartSymbolEnabled',
              symbolUniverse: 'symbolUniverse',
              hvMode: 'hvMode', priceActionMode: 'priceActionMode',
              tradingHoursEnabled: 'tradingHoursEnabled',
              tradingHoursStart: 'tradingHoursStart', tradingHoursEnd: 'tradingHoursEnd',
              goldenHoursMode: 'goldenHoursMode', goldenRepeatInterval: 'goldenRepeatInterval',
              goldenRepeatCount: 'goldenRepeatCount',
              goldenHourMon: 'goldenHourMon', goldenHourTue: 'goldenHourTue',
              goldenHourWed: 'goldenHourWed', goldenHourThu: 'goldenHourThu',
              goldenHourFri: 'goldenHourFri', goldenHourSat: 'goldenHourSat',
              goldenHourSun: 'goldenHourSun',
              scalpGateEnabled: 'scalpGateEnabled', scalpGateProfile: 'scalpGateProfile',
              impulseGateEnabled: 'impulseGateEnabled', impulseGatePreset: 'impulseGatePreset',
              impulseGateFailAction: 'impulseGateFailAction',
              executionMode: 'executionMode',
              limitFirstAllowMarketFallback: 'limitFirstAllowMarketFallback',
              limitFirstMaxEntrySlipPct: 'limitFirstMaxEntrySlipPct',
              limitFirstTimeoutMs: 'limitFirstTimeoutMs',
              strategyBudgetPlannerEnabled: 'strategyBudgetPlannerEnabled',
              strategyBudgetUsd: 'strategyBudgetUsd',
              budgetBufferPct: 'budgetBufferPct', feeModel: 'feeModel',
              feeRtPctMargin: 'feeRtPctMargin', maxExposureMult: 'maxExposureMult',
              maxRescueAddPctOfSlot: 'maxRescueAddPctOfSlot', minTradeNotional: 'minTradeNotional',
              reactiveTrailingEnabled: 'reactiveTrailingEnabled',
              netLossCapEnabled: 'netLossCapEnabled', netLossCapPercent: 'netLossCapPercent',
              fixedTpEnabled: 'fixedTpEnabled', fixedTpPercent: 'fixedTpPercent',
              modeId: 'modeId', modeName: 'modeName', modeSource: 'modeSource',
              enabled: 'enabled',
              indicatorStackEnabled: 'indicatorStackEnabled',
              // v4.5.4: Rescue Ladder settings (were saved to KV but never reloaded)
              rescueLevels: 'rescueLevels',
              rescueTriggerThresholdPct: 'rescueTriggerThresholdPct',
              rescuePremiumPct: 'rescuePremiumPct',
              rescueCooldownSec: 'rescueCooldownSec',
              hardLossWallPct: 'hardLossWallPct',
              tslBehavior: 'tslBehavior',
              manualTriggerThresholdPct: 'manualTriggerThresholdPct',
              // v4.9.0: Rescue safety caps (were used with defaults but never saved/reloaded)
              maxSingleRescuePctOfBudget: 'maxSingleRescuePctOfBudget',
              maxTotalExposurePctPerSymbol: 'maxTotalExposurePctPerSymbol',
              perSymbolMaxExposurePct: 'perSymbolMaxExposurePct',
              trueBEBufferPct: 'trueBEBufferPct',
              // v4.5.4: Zero-Lock + Decision Model (were saved to KV but never reloaded)
              zeroLockTrailingEnabled: 'zeroLockTrailingEnabled',
              decisionProfile: 'decisionProfile',
              directionStackEnabled: 'directionStackEnabled',
              autoCloseAtTP1: 'autoCloseAtTP1',
              velocityCrashEnabled: 'velocityCrashEnabled',
              hardLossCapNet: 'hardLossCapNet',
              // v4.5.4: Budget Sync (were saved to KV but never reloaded)
              budgetSourceMode: 'budgetSourceMode',
              budgetCapUsd: 'budgetCapUsd',
              budgetSyncCadenceSec: 'budgetSyncCadenceSec',
              staleBudgetTimeoutSec: 'staleBudgetTimeoutSec',
              crossStrategyMode: 'crossStrategyMode',
              crossStrategyWeight: 'crossStrategyWeight',
              // v4.5.4: Profit-Only Trailing (were saved to KV but never reloaded)
              profitTrailDistanceNet: 'profitTrailDistanceNet',
              beFloorBufferPercent: 'beFloorBufferPercent',
              slippageBufferPercent: 'slippageBufferPercent',
              // v4.5.4: Reactive Trailing Engine extras
              reactiveTrailingMode: 'reactiveTrailingMode',
              reactiveTrailingTickMs: 'reactiveTrailingTickMs',
              reactiveTrailingOnlyWhenArmed: 'reactiveTrailingOnlyWhenArmed',
              reactiveTrailingMaxDurationSec: 'reactiveTrailingMaxDurationSec',
              // v4.5.4: Smart Exit Enhancements
              intentAutoDetectEnabled: 'intentAutoDetectEnabled',
              adaptiveProfitTrailEnabled: 'adaptiveProfitTrailEnabled',
              adaptiveProfitTrailATRMultiplier: 'adaptiveProfitTrailATRMultiplier',
              trailingStopFloorEnabled: 'trailingStopFloorEnabled',
              multiTargetTPEnabled: 'multiTargetTPEnabled',
              multiTargetTightenAfterR1: 'multiTargetTightenAfterR1',
              zeroLockMeaningfulEnabled: 'zeroLockMeaningfulEnabled',
              zeroLockMeaningfulRule: 'zeroLockMeaningfulRule',
              zeroLockSustainedTicks: 'zeroLockSustainedTicks',
              // v4.7.0: Two-Stage Zero-Lock + Rescue Win-Conversion Timebox
              zeroLockStage2Threshold: 'zeroLockStage2Threshold',
              rescueWinConversionTimeoutMin: 'rescueWinConversionTimeoutMin',
              aiCloseGuardEnabled: 'aiCloseGuardEnabled',
              aiCloseGuardMinNetProfit: 'aiCloseGuardMinNetProfit',
              aiCloseGuardMinTPFraction: 'aiCloseGuardMinTPFraction',
              // v4.5.4: Experience Mode
              experienceModeEnabled: 'experienceModeEnabled',
              // v4.5.4: Win Rate Boost Pack
              microImpulseGateEnabled: 'microImpulseGateEnabled',
              fastToGreenTimeoutEnabled: 'fastToGreenTimeoutEnabled',
              mode1AntiChopStrictEnabled: 'mode1AntiChopStrictEnabled',
              // v4.5.4: Side Policy + Active Actions
              sidePolicy: 'sidePolicy',
              activeActions: 'activeActions',
              // v4.5.4: Fast Green Scalp + full FGS settings
              fastGreenScalpEnabled: 'fastGreenScalpEnabled',
              fgsTrendOnlyPolicy: 'fgsTrendOnlyPolicy',
              fgsAllowRangeScalpsInNeutralHTF: 'fgsAllowRangeScalpsInNeutralHTF',
              fastGreenTimeoutSec: 'fastGreenTimeoutSec',
              scalpTimeboxMinutes: 'scalpTimeboxMinutes',
              partialTpPercent: 'partialTpPercent',
              fgsLossCapBaseSLGross: 'fgsLossCapBaseSLGross',
              fgsLossCapKSL: 'fgsLossCapKSL',
              fgsHardMaxLossNet: 'fgsHardMaxLossNet',
              fgsSymbolQualityFilterEnabled: 'fgsSymbolQualityFilterEnabled',
              fgsSymbolBlacklistHours: 'fgsSymbolBlacklistHours',
              // v4.5.4: Scalp Gate pipeline settings
              scalpGateApplyOn5x: 'scalpGateApplyOn5x',
              scalpGateApplyOn10x: 'scalpGateApplyOn10x',
              scalpGateApplyOn20x: 'scalpGateApplyOn20x',
              disable30mTrendFilter: 'disable30mTrendFilter',
              disable5mConfirmationGate: 'disable5mConfirmationGate',
              disableConsensusGate: 'disableConsensusGate',
              disableExhaustionGate: 'disableExhaustionGate',
              disableImpulseGate: 'disableImpulseGate',
              disableEntryTimingNear15mHigh: 'disableEntryTimingNear15mHigh',
              priceDriftGateMode: 'priceDriftGateMode',
              chopPolicy: 'chopPolicy',
              widenEntryTimingPercent: 'widenEntryTimingPercent',
              // Signal validity + first-tick safety
              signalValidityMinutes: 'signalValidityMinutes',
              firstTickExtraCapPct: 'firstTickExtraCapPct',
            };
            for (const [kvKey, settingsKey] of Object.entries(fieldMap)) {
              if (scheduleData[kvKey] !== undefined && scheduleData[kvKey] !== null) {
                kvOverrides[settingsKey] = boolToYesNo(scheduleData[kvKey]);
              }
            }
            if (Object.keys(kvOverrides).length > 0) {
              console.log("[SCHEDULER] Merged", Object.keys(kvOverrides).length, "KV schedule values into settings");
              setSettings(prev => ({ ...prev, ...kvOverrides }));
              // v4.5.4: Sync derived state from KV-loaded values
              if (kvOverrides.modeId) setSelectedModeId(kvOverrides.modeId);
              if (kvOverrides.activeActions) setSelectedActions(kvOverrides.activeActions);
            }
          }
        } catch (error) {
          console.log("[SCHEDULER] Could not fetch schedule info:", error.message);
        }
      };
      
      fetchScheduleInfo();
      // Refresh every 30 seconds
      const interval = setInterval(fetchScheduleInfo, 30000);
      return () => clearInterval(interval);
    }
  }, [node?.type, strategyId]);

  // Update countdown timer every second
  useEffect(() => {
    if (nextExecutionInfo?.timestamp) {
      const updateCountdown = () => {
        const now = new Date();
        const nextRun = new Date(nextExecutionInfo.timestamp);
        const diffMs = nextRun - now;
        
        if (diffMs <= 0) {
          setCountdown("Running now...");
          return;
        }
        
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
        
        if (hours > 0) {
          setCountdown(`${hours}h ${minutes}m ${seconds}s`);
        } else if (minutes > 0) {
          setCountdown(`${minutes}m ${seconds}s`);
        } else {
          setCountdown(`${seconds}s`);
        }
      };
      
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [nextExecutionInfo?.timestamp]);

  // Handle model change
  const handleModelChange = (event) => {
    const model = event.target.value;
    setSelectedModel(model);

    // Check if assistant is supported for this model
    const modelInfo = AI_MODELS.find((m) => m.id === model);
    if (!modelInfo.assistantSupport) {
      setUseAssistant(false);
    }
  };

  // Handle prompt change
  const handlePromptChange = (event) => {
    setCustomPrompt(event.target.value);
  };

  // Reset prompt to default
  const handleResetPrompt = () => {
    setCustomPrompt(DEFAULT_PROMPTS[node?.type] || "");
  };

  // Handle assistant toggle
  const handleAssistantToggle = (event) => {
    setUseAssistant(event.target.checked);
  };

  // Handle assistant ID change
  const handleAssistantIdChange = (event) => {
    setAssistantId(event.target.value);
  };

  // Handle generic setting change
  const handleSettingChange = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  // Test connection to the selected AI model
  const handleTestConnection = async () => {
    setTestStatus("loading");
    setTestMessage("Testing connection...");

    try {
      // In a real app, this would call an API to test the connection
      // Simulate API call with timeout
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // For demonstration, we'll simulate success/failure based on having an API key
      // In a real app, this would be an actual API call to test the connection
      const selectedProvider = AI_MODELS.find(
        (m) => m.id === selectedModel,
      )?.provider;

      // Assume success for demo purposes
      setTestStatus("success");
      setTestMessage(
        `Successfully connected to ${selectedProvider.toUpperCase()} API`,
      );
    } catch (error) {
      setTestStatus("error");
      setTestMessage(`Connection failed: ${error.message}`);
    }
  };

  // Test a social media API connection
  const testSocialMediaConnection = useCallback(async (apiId) => {
    setApiTestStatus((prev) => ({
      ...prev,
      [apiId]: "loading",
    }));
    setApiTestMessages((prev) => ({
      ...prev,
      [apiId]: "Testing connection...",
    }));

    try {
      // Simulate API call with timeout
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // For demo purposes, simulate success
      setApiTestStatus((prev) => ({
        ...prev,
        [apiId]: "success",
      }));
      setApiTestMessages((prev) => ({
        ...prev,
        [apiId]: `Successfully connected to ${apiId.toUpperCase()} API`,
      }));
    } catch (error) {
      setApiTestStatus((prev) => ({
        ...prev,
        [apiId]: "error",
      }));
      setApiTestMessages((prev) => ({
        ...prev,
        [apiId]: `Connection failed: ${error.message}`,
      }));
    }
  }, []);

  // Test a market data API connection
  const testMarketDataConnection = useCallback(async (apiId) => {
    setApiTestStatus((prev) => ({
      ...prev,
      [apiId]: "loading",
    }));
    setApiTestMessages((prev) => ({
      ...prev,
      [apiId]: "Testing connection...",
    }));

    try {
      // Simulate API call with timeout
      await new Promise((resolve) => setTimeout(resolve, 1200));

      // For demo purposes, simulate success
      setApiTestStatus((prev) => ({
        ...prev,
        [apiId]: "success",
      }));
      setApiTestMessages((prev) => ({
        ...prev,
        [apiId]: `Successfully connected to ${apiId.toUpperCase()} API`,
      }));
    } catch (error) {
      setApiTestStatus((prev) => ({
        ...prev,
        [apiId]: "error",
      }));
      setApiTestMessages((prev) => ({
        ...prev,
        [apiId]: `Connection failed: ${error.message}`,
      }));
    }
  }, []);

  // Handle toggling social media API connection
  const handleToggleSocialMedia = useCallback(
    (apiId, enabled) => {
      setSocialMediaConnections((prev) => ({
        ...prev,
        [apiId]: enabled,
      }));

      // If enabling, test the connection
      if (enabled) {
        testSocialMediaConnection(apiId);
      } else {
        // Update status when disabled
        setApiTestStatus((prev) => ({
          ...prev,
          [apiId]: null,
        }));
        setApiTestMessages((prev) => ({
          ...prev,
          [apiId]: "",
        }));
      }
    },
    [testSocialMediaConnection],
  );

  // Handle toggling market data API connection
  const handleToggleMarketData = useCallback(
    (apiId, enabled) => {
      setMarketDataConnections((prev) => ({
        ...prev,
        [apiId]: enabled,
      }));

      // If enabling, test the connection
      if (enabled) {
        testMarketDataConnection(apiId);
      } else {
        // Update status when disabled
        setApiTestStatus((prev) => ({
          ...prev,
          [apiId]: null,
        }));
        setApiTestMessages((prev) => ({
          ...prev,
          [apiId]: "",
        }));
      }
    },
    [testMarketDataConnection],
  );

  // Handle testing API connections (social media, market data, or crypto analytics)
  const handleTestApi = async (apiType, apiId) => {
    const statusKey =
      apiType === "social_media"
        ? apiId
        : apiType === "market_data"
          ? `market_${apiId}`
          : apiType === "crypto_analytics"
            ? `crypto_${apiId}`
            : apiId;

    setApiTestStatus((prev) => ({
      ...prev,
      [statusKey]: "loading",
    }));

    setApiTestMessages((prev) => ({
      ...prev,
      [statusKey]: "Testing connection...",
    }));

    try {
      // In a real app, this would make an API call through the system
      // Simulate API call with timeout
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // For demo purposes, simulate 90% success rate
      const success = Math.random() < 0.9;

      if (success) {
        setApiTestStatus((prev) => ({
          ...prev,
          [statusKey]: "success",
        }));

        setApiTestMessages((prev) => ({
          ...prev,
          [statusKey]: `Successfully connected to ${apiId}`,
        }));
      } else {
        throw new Error("Authentication failed");
      }
    } catch (error) {
      setApiTestStatus((prev) => ({
        ...prev,
        [statusKey]: "error",
      }));

      setApiTestMessages((prev) => ({
        ...prev,
        [statusKey]: `Error: ${error.message}`,
      }));
    }
  };

  // Get icon for social media APIs
  const getSocialMediaIcon = (apiId) => {
    switch (apiId) {
      case "twitter":
        return <TwitterIcon color="primary" />;
      case "reddit":
        return <RedditIcon color="primary" />;
      case "stocktwits":
        return <ChatIcon color="primary" />;
      case "linkedin":
        return <LinkedInIcon color="primary" />;
      case "youtube":
        return <YouTubeIcon color="primary" />;
      default:
        return <DataUsageIcon color="primary" />;
    }
  };

  // Get icon for crypto analytics APIs
  const getCryptoAnalyticsIcon = (apiId) => {
    switch (apiId) {
      case "whaleAlert":
      case "coinbaseCloud":
        return <CurrencyBitcoinIcon color="primary" />;
      case "glassnode":
      case "etherscan":
        return <TimelineIcon color="primary" />;
      case "santiment":
        return <ShowChartIcon color="primary" />;
      case "nansen":
      case "duneAnalytics":
        return <DataUsageIcon color="primary" />;
      case "arkhamIntelligence":
        return <MonetizationOnIcon color="primary" />;
      case "cryptoQuant":
        return <BarChartIcon color="primary" />;
      case "intoTheBlock":
        return <TokenIcon color="primary" />;
      default:
        return <CurrencyBitcoinIcon color="primary" />;
    }
  };

  // Get icon for market data APIs
  const getMarketDataIcon = (apiId) => {
    switch (apiId) {
      case "alphavantage":
      case "financeapi":
        return <BarChartIcon color="primary" />;
      case "yahoofinance":
        return <DataUsageIcon color="primary" />;
      case "bloomberg":
      case "newsapi":
        return <NewspaperIcon color="primary" />;
      case "sec-edgar":
        return <PagesIcon color="primary" />;
      default:
        return <DataUsageIcon color="primary" />;
    }
  };

  // Handle toggling crypto analytics API connections
  const handleToggleCryptoAnalytics = (apiId, checked) => {
    setCryptoAnalyticsConnections({
      ...cryptoAnalyticsConnections,
      [apiId]: checked,
    });
  };

  // Test Smart Symbol Discovery
  const handleTestSmartSymbol = async () => {
    setSmartSymbolLoading(true);
    setSmartSymbolResult(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_STRATEGY_ENGINE_URL || "https://strategy-engine.houman-ghavamzadeh.workers.dev"}/api/phase/smart-symbol/discover`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            universeId: selectedUniverse,
            brokers: selectedBrokers,
            defaultDirection: "HOLD",
          }),
        },
      );

      const data = await response.json();
      if (data.success) {
        setSmartSymbolResult(data.data);
      } else {
        setSmartSymbolResult({ error: data.error || "Discovery failed" });
      }
    } catch (error) {
      setSmartSymbolResult({ error: error.message });
    } finally {
      setSmartSymbolLoading(false);
    }
  };

  // Save settings
  const handleSave = async () => {
    const updatedSettings = {
      ...settings,
      aiModel: selectedModel,
      customPrompt,
      useAssistant,
      assistantId: useAssistant ? assistantId : "",
      // Smart Symbol settings
      smartSymbolEnabled,
      symbolUniverse: settings.symbolUniverse || selectedUniverse || "major_crypto",
      selectedBrokers,
      customSymbols,
      minConfidenceThreshold,
      feeAwareSelection,
      // Add social media and market data connections for fundamental analyst nodes
      ...(node?.type === "fundamentalAnalyst"
        ? {
            socialMediaConnections,
            marketDataConnections,
            cryptoAnalyticsConnections,
          }
        : {}),
    };

    // For AI Schedule nodes, save config to backend
    if (node?.type === "aiSchedule" && strategyId) {
      setScheduleSaving(true);
      setScheduleError(null);
      try {
        const allModeIds = 'many_small_wins_scalp_5x,many_small_wins_scalp_10x,many_small_wins_scalp_20x,fewer_larger_wins_hybrid_5x,fewer_larger_wins_trend_10x,pro_trader_brain_5x,pro_trader_brain_10x,pro_trader_brain_20x';
        const aiScheduleData = {
          enabled: true,
          aiModeEnabled: settings.aiModeEnabled === true || settings.aiModeEnabled === "Yes",
          experimentEnabled: settings.experimentEnabled === true || settings.experimentEnabled === "Yes",
          tradeWeekends: settings.tradeWeekends === true || settings.tradeWeekends === "Yes",
          workingDays: (settings.workingDays || "Mon,Tue,Wed,Thu,Fri").split(","),
          timezone: settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          rotationPolicy: settings.rotationPolicy || "round_robin",
          allowedModeIds: (settings.allowedModeIds || allModeIds).split(","),
          hardRails: {
            maxDailyLossPct: parseFloat(settings.maxDailyLossPct) || 3,
            maxDrawdownPct: parseFloat(settings.maxDrawdownPct) || 10,
            maxLeverageByVol: true,
            maxSpreadPct: parseFloat(settings.maxSpreadPct) || 0.15,
            maxConcurrentPositions: parseInt(settings.maxConcurrentPositions) || 5,
            correlationCapEnabled: settings.correlationCapEnabled !== false,
            feeEdgeGuardEnabled: settings.feeEdgeGuardEnabled !== false,
          },
          baseSchedule: {
            broker: settings.aiBroker || "kucoin",
            tradingMode: settings.aiTradingMode || "futures",
            leverage: parseInt(settings.aiLeverage) || 5,
            positionSizeUsd: settings.aiPositionSizeUsd || "100",
            symbolUniverse: settings.aiSymbolUniverse || "all_crypto",
            scanInterval: settings.aiScanInterval || "5m",
            paperTrading: settings.aiPaperTrading === "Yes" || settings.aiPaperTrading === true ? "Yes" : "No",
            confidenceThreshold: settings.aiConfidenceThreshold || "82",
          },
        };
        const baseUrl = StrategyEngineService.baseUrl;
        const resp = await fetch(`${baseUrl}/api/admin/ai-schedule/${strategyId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(aiScheduleData),
        });
        if (!resp.ok) {
          const errBody = await resp.json().catch(() => ({}));
          throw new Error(errBody.error || `Server returned ${resp.status}`);
        }
        console.log("[AI-SCHEDULE] Config saved for strategy:", strategyId);
      } catch (error) {
        console.error("[AI-SCHEDULE] Error saving config:", error);
        setScheduleError(error.message || "Failed to save AI schedule config");
      } finally {
        setScheduleSaving(false);
      }
    }

    // For Scheduler nodes and modular strategy nodes, create/update the schedule in the backend
    const isSchedulerType = node?.type === "scheduler"
      || node?.type === "entryGate" || node?.type === "exitEngine"
      || node?.type === "rescueManager" || node?.type === "budgetAllocator"
      || node?.type === "decisionEngine";
    if (isSchedulerType && strategyId) {
      setScheduleSaving(true);
      setScheduleError(null);
      try {
        // Use settings values first, fall back to defaults only if not set
        const defaults = node?.data?.defaultParams || {};
        const scheduleData = {
          // v4.4.9: Only send enabled if explicitly set in settings (from KV merge).
          // If undefined, omit it so the backend preserves existing toggle state.
          ...(settings.enabled !== undefined ? { enabled: settings.enabled !== "No" && settings.enabled !== false } : {}),
          // Dual-interval scheduling
          scanInterval: settings.scanInterval || defaults.interval || "5m",
          manageInterval: settings.manageInterval || "1m",
          broker: settings.broker || defaults.broker || "kucoin",
          tradingMode: settings.tradingMode || "spot", // spot, margin, futures
          leverage: (settings.tradingMode === 'margin' || settings.tradingMode === 'futures') ? (settings.leverage || 1) : 1,
          leverageEntryMode: settings.leverageEntryMode || "strict",
          confidenceThreshold: settings.confidenceThreshold || defaults.confidenceThreshold || "82",
          // autoExecute and confirmationMode are linked: autoExecute ON = confirmationMode OFF, and vice versa
          autoExecute: settings.autoExecute === "Yes" || settings.autoExecute === true ? "Yes" : "No",
          confirmationMode: settings.autoExecute === "Yes" || settings.autoExecute === true ? "No" : "Yes",
          maxSignalsPerRun: settings.maxSignalsPerRun || node?.data?.defaultParams?.maxSignalsPerRun || "5",
          paperTrading: settings.paperTrading || node?.data?.defaultParams?.paperTrading || "Yes",
          positionSizeUsd: settings.positionSizeUsd || node?.data?.defaultParams?.positionSizeUsd || "100",
          positionSizePercent: settings.positionSizePercent || node?.data?.defaultParams?.positionSizePercent || "5",
          exitStrategy: settings.exitStrategy || "ai_moderate",
          stopLossPercent: settings.stopLossPercent || node?.data?.defaultParams?.stopLossPercent || "1.0",
          takeProfitPercent: settings.takeProfitPercent || node?.data?.defaultParams?.takeProfitPercent || "2.5",
          useTrailingStop: settings.useTrailingStop !== undefined ? settings.useTrailingStop : (node?.data?.defaultParams?.useTrailingStop ?? "Yes"),
          trailingStopAiMode: settings.trailingStopAiMode === "Yes" || settings.trailingStopAiMode === true,
          trailingStopPercent: settings.trailingStopPercent || node?.data?.defaultParams?.trailingStopPercent || "0.6",
          cooldownMinutes: settings.cooldownMinutes || node?.data?.defaultParams?.cooldownMinutes || "3",
          cooldownPerSymbol: settings.cooldownPerSymbol !== undefined ? settings.cooldownPerSymbol : (node?.data?.defaultParams?.cooldownPerSymbol ?? "Yes"),
          retryOnFailure: settings.retryOnFailure !== undefined ? settings.retryOnFailure : (node?.data?.defaultParams?.retryOnFailure ?? "Yes"),
          maxRetries: settings.maxRetries || node?.data?.defaultParams?.maxRetries || "3",
          retryDelaySeconds: settings.retryDelaySeconds || node?.data?.defaultParams?.retryDelaySeconds || "60",
          notifyOnTrade: settings.notifyOnTrade !== undefined ? settings.notifyOnTrade : (node?.data?.defaultParams?.notifyOnTrade ?? "Yes"),
          notifyOnError: settings.notifyOnError !== undefined ? settings.notifyOnError : (node?.data?.defaultParams?.notifyOnError ?? "Yes"),
          telegramEnabled: settings.telegramEnabled !== undefined ? settings.telegramEnabled : (node?.data?.defaultParams?.telegramEnabled ?? "No"),
          // dryRunMode removed — use paperTrading instead (v3.9.9)
          // Store timezone and convert trading hours to UTC
          timezone: settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          tradingHoursStart: (() => {
            const localTime = settings.tradingHoursStart || node?.data?.defaultParams?.tradingHoursStart || "00:00";
            const timezone = settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            // Get timezone offset for the selected timezone
            const now = new Date();
            const localDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
            const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
            const offsetHours = (localDate - utcDate) / (1000 * 60 * 60);
            const [hours, mins] = localTime.split(':').map(Number);
            const utcHours = (hours - offsetHours + 24) % 24;
            return `${String(Math.floor(utcHours)).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
          })(),
          tradingHoursEnd: (() => {
            const localTime = settings.tradingHoursEnd || node?.data?.defaultParams?.tradingHoursEnd || "23:59";
            const timezone = settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            const now = new Date();
            const localDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
            const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
            const offsetHours = (localDate - utcDate) / (1000 * 60 * 60);
            const [hours, mins] = localTime.split(':').map(Number);
            const utcHours = (hours - offsetHours + 24) % 24;
            return `${String(Math.floor(utcHours)).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
          })(),
          daysActive: settings.daysActive || node?.data?.defaultParams?.daysActive || "Mon,Tue,Wed,Thu,Fri,Sat,Sun",
          skipOpenSymbols: settings.skipOpenSymbols === "Yes" || settings.skipOpenSymbols === true ? "Yes" : "No",
          
          // AI Money Management settings
          moneyManagementEnabled: settings.moneyManagementEnabled === "Yes" || settings.moneyManagementEnabled === true,
          maxConcurrentPositions: parseInt(settings.maxConcurrentPositions) || 20,
          allocationMode: settings.allocationMode || "equal",
          reservePercent: parseFloat(settings.reservePercent) || 20,
          maxPerTrade: parseFloat(settings.maxPerTrade) || 5,
          maxPerSymbol: parseFloat(settings.maxPerSymbol) || 10,
          minTradeNotional: parseFloat(settings.minTradeNotional) || 10,
          rescueScaleInEnabled: settings.rescueScaleInEnabled === "Yes" || settings.rescueScaleInEnabled === true,
          rescueReservePercent: parseFloat(settings.rescueReservePercent) || 5,
          maxRescueAdds: parseInt(settings.maxRescueAdds) || 1,
          // v4.1.3: Rescue controls (were in UI but never sent to backend)
          rescueMode: settings.rescueMode || 'AI_ONLY',
          manualTriggerThresholdPct: parseFloat(settings.manualTriggerThresholdPct) || 3,
          maxRescueAttempts: parseInt(settings.maxRescueAttempts) || 3,
          rescueAddPremiumPct: parseFloat(settings.rescueAddPremiumPct) || 10,
          rescueAddPremiumAI: settings.rescueAddPremiumAI === true || settings.rescueAddPremiumAI === "Yes",
          // vNext: Rescue Ladder settings (trailing/zero-lock aware)
          rescueLevels: parseInt(settings.rescueLevels) || parseInt(settings.maxRescueAdds) || 1,
          rescueTriggerThresholdPct: parseFloat(settings.rescueTriggerThresholdPct) || -3.0,
          rescuePremiumPct: parseFloat(settings.rescuePremiumPct) || 10,
          rescueCooldownSec: parseInt(settings.rescueCooldownSec) || 45,
          hardLossWallPct: parseFloat(settings.hardLossWallPct) || -8.0,
          // v4.9.0: Rescue safety caps
          maxSingleRescuePctOfBudget: parseFloat(settings.maxSingleRescuePctOfBudget) || 15,
          maxTotalExposurePctPerSymbol: parseFloat(settings.maxTotalExposurePctPerSymbol) || 25,
          perSymbolMaxExposurePct: parseFloat(settings.perSymbolMaxExposurePct) || 15,
          trueBEBufferPct: parseFloat(settings.trueBEBufferPct) || 0.10,
          tslBehavior: settings.tslBehavior || 'DISABLE_TSL_UNTIL_RESCUES_SPENT',
          // vNext: Zero-Lock settings
          zeroLockTrailingEnabled: settings.zeroLockTrailingEnabled !== false && settings.zeroLockTrailingEnabled !== "No",
          // Strategy Budget Planner (v4.1.0) + Budget Sync (v4.2.0)
          strategyBudgetPlannerEnabled: settings.strategyBudgetPlannerEnabled === true || settings.strategyBudgetPlannerEnabled === "Yes",
          strategyBudgetUsd: parseFloat(settings.strategyBudgetUsd) || 1000,
          budgetBufferPct: parseFloat(settings.budgetBufferPct) || 10,
          feeModel: settings.feeModel || 'auto',
          feeRtPctMargin: parseFloat(settings.feeRtPctMargin) || 0.60,
          maxExposureMult: parseFloat(settings.maxExposureMult) || 3.0,
          maxRescueAddPctOfSlot: parseFloat(settings.maxRescueAddPctOfSlot) || 60,
          // v4.5.3: Budget Sync — live broker balance for real trading
          budgetSourceMode: settings.budgetSourceMode || 'MANUAL_FIXED_BUDGET',
          budgetCapUsd: parseFloat(settings.budgetCapUsd) || 0,
          budgetSyncCadenceSec: parseInt(settings.budgetSyncCadenceSec) || 15,
          staleBudgetTimeoutSec: parseInt(settings.staleBudgetTimeoutSec) || 60,
          crossStrategyMode: settings.crossStrategyMode || 'PER_STRATEGY_ISOLATED',
          crossStrategyWeight: parseFloat(settings.crossStrategyWeight) || 1.0,
          
          // Volume Control settings
          enableVolumeControl: settings.enableVolumeControl !== "No" && settings.enableVolumeControl !== false,
          volumeStrictness: settings.volumeStrictness || "medium",
          
          // Strict Entry Confirmation
          strictEntryEnabled: settings.strictEntryEnabled === "Yes" || settings.strictEntryEnabled === true,
          
          // v4.1.3: Sell Filter + ML Gate (were in UI but never sent to backend)
          sellFilterStrict: settings.sellFilterStrict === true || settings.sellFilterStrict === "Yes",
          enableMLGate: settings.enableMLGate === true || settings.enableMLGate === "Yes",
          
          // Cross-Asset Correlation
          enableCorrelation: settings.enableCorrelation !== "No" && settings.enableCorrelation !== false,
          
          // Multi-Timeframe Trend Direction Filters
          trendFilter1m: settings.trendFilter1m === true || settings.trendFilter1m === "Yes",
          trendFilter3m: settings.trendFilter3m === true || settings.trendFilter3m === "Yes",
          trendFilter5m: settings.trendFilter5m === true || settings.trendFilter5m === "Yes",
          trendFilter15m: settings.trendFilter15m === true || settings.trendFilter15m === "Yes",
          trendFilter30m: settings.trendFilter30m !== "No" && settings.trendFilter30m !== false,
          trendFilter1h: settings.trendFilter1h === true || settings.trendFilter1h === "Yes",
          trendFilter4h: settings.trendFilter4h === true || settings.trendFilter4h === "Yes",
          
          // v4.4.11: Last Candle Confirmation Filter
          lastCandleFilter1m: settings.lastCandleFilter1m === true || settings.lastCandleFilter1m === "Yes",
          lastCandleFilter3m: settings.lastCandleFilter3m === true || settings.lastCandleFilter3m === "Yes",
          lastCandleFilter5m: settings.lastCandleFilter5m === true || settings.lastCandleFilter5m === "Yes",
          lastCandleFilter15m: settings.lastCandleFilter15m === true || settings.lastCandleFilter15m === "Yes",
          lastCandleFilter30m: settings.lastCandleFilter30m === true || settings.lastCandleFilter30m === "Yes",
          lastCandleFilter1h: settings.lastCandleFilter1h === true || settings.lastCandleFilter1h === "Yes",
          lastCandleFilter4h: settings.lastCandleFilter4h === true || settings.lastCandleFilter4h === "Yes",
          
          // High-Velocity Assets Mode
          hvMode: settings.hvMode === "Yes" || settings.hvMode === true,
          hvMinVolatility: parseFloat(settings.hvMinVolatility) || 8,
          hvMinVolume: parseFloat(settings.hvMinVolume) || 1000000,
          hvMaxListingAge: parseInt(settings.hvMaxListingAge) || 60,
          hvSpreadGuard: parseFloat(settings.hvSpreadGuard) || 0.3,
          hvExcludeStablecoins: settings.hvExcludeStablecoins !== "No" && settings.hvExcludeStablecoins !== false,
          hvExcludeMajorCaps: settings.hvExcludeMajorCaps !== "No" && settings.hvExcludeMajorCaps !== false,
          hvMaxHoldTime: parseInt(settings.hvMaxHoldTime) || 45,
          hvProfitLadder: settings.hvProfitLadder !== "No" && settings.hvProfitLadder !== false,
          hvProfitLevels: settings.hvProfitLevels || "1.5,3,5",
          hvEmergencyVolumeExit: settings.hvEmergencyVolumeExit !== "No" && settings.hvEmergencyVolumeExit !== false,
          hvMaxRiskPerTrade: parseFloat(settings.hvMaxRiskPerTrade) || 0.5,
          hvMaxConcurrent: parseInt(settings.hvMaxConcurrent) || 2,
          hvDisableRescue: settings.hvDisableRescue !== "No" && settings.hvDisableRescue !== false,
          hvMaxLeverage: parseInt(settings.hvMaxLeverage) || 1,
          
          // Price Action Mode settings
          priceActionMode: settings.priceActionMode === "Yes" || settings.priceActionMode === true,
          priceActionEntryTF: settings.priceActionEntryTF || "5m",
          priceActionTrendTF: settings.priceActionTrendTF || "1h",
          paDetectSR: settings.paDetectSR !== "No" && settings.paDetectSR !== false,
          paDetectBreakout: settings.paDetectBreakout !== "No" && settings.paDetectBreakout !== false,
          paDetectCandlestick: settings.paDetectCandlestick !== "No" && settings.paDetectCandlestick !== false,
          paDetectTrendline: settings.paDetectTrendline !== "No" && settings.paDetectTrendline !== false,
          paDetectFibonacci: settings.paDetectFibonacci !== "No" && settings.paDetectFibonacci !== false,
          paDetectDivergence: settings.paDetectDivergence !== "No" && settings.paDetectDivergence !== false,
          paDetectBumpAndRun: settings.paDetectBumpAndRun === "Yes" || settings.paDetectBumpAndRun === true,
          
          // Trailing Benefit — always ON, always AI-managed (v3.9.9)
          useTrailingBenefit: true,
          trailingBenefitAiMode: true,
          
          // v3.9.0: Profit-Only Trailing / No-Red Exit settings
          profitTrailDistanceNet: parseFloat(settings.profitTrailDistanceNet) || 0.50,
          beFloorBufferPercent: parseFloat(settings.beFloorBufferPercent) || 0.00,
          slippageBufferPercent: parseFloat(settings.slippageBufferPercent) || 0.10,
          
          // v4.1.3: Fixed TP Ceiling (was in UI but never sent to backend)
          fixedTpEnabled: settings.fixedTpEnabled === true || settings.fixedTpEnabled === "Yes",
          fixedTpPercent: parseFloat(settings.fixedTpPercent) || 0,
          
          // v3.9.4: Reactive Trailing Engine (RTE) settings
          reactiveTrailingEnabled: settings.reactiveTrailingEnabled === "Yes" || settings.reactiveTrailingEnabled === true,
          reactiveTrailingMode: settings.reactiveTrailingMode || "legacy",
          reactiveTrailingTickMs: parseInt(settings.reactiveTrailingTickMs) || 1000,
          reactiveTrailingOnlyWhenArmed: settings.reactiveTrailingOnlyWhenArmed !== "No" && settings.reactiveTrailingOnlyWhenArmed !== false,
          reactiveTrailingMaxDurationSec: parseInt(settings.reactiveTrailingMaxDurationSec) || 600,
          
          // v3.9.5: Impulse Gate (default ON)
          impulseGateEnabled: settings.impulseGateEnabled !== "No" && settings.impulseGateEnabled !== false,
          impulseGatePreset: settings.impulseGatePreset || "auto",
          impulseGateFailAction: settings.impulseGateFailAction || "PENDING",
          
          // v3.9.5: Limit-First Execution (default ON)
          executionMode: settings.executionMode || "limit_first",
          limitFirstAllowMarketFallback: settings.limitFirstAllowMarketFallback !== "No" && settings.limitFirstAllowMarketFallback !== false,
          limitFirstMaxEntrySlipPct: parseFloat(settings.limitFirstMaxEntrySlipPct) || 0.08,
          limitFirstMicroChaseSteps: parseInt(settings.limitFirstMicroChaseSteps) || 1,
          limitFirstChaseStepPct: parseFloat(settings.limitFirstChaseStepPct) || 0.02,
          limitFirstTimeoutMs: parseInt(settings.limitFirstTimeoutMs) || 600,
          
          // v3.9.7: Win Rate Boost Pack
          microImpulseGateEnabled: settings.microImpulseGateEnabled === "Yes" || settings.microImpulseGateEnabled === true,
          microImpulseGateMinPass: parseInt(settings.microImpulseGateMinPass) || 3,
          microImpulseAdxMin: parseFloat(settings.microImpulseAdxMin) || 18,
          microImpulseVolumeBurst1m: parseFloat(settings.microImpulseVolumeBurst1m) || 1.6,
          microImpulseVolumeBurst5m: parseFloat(settings.microImpulseVolumeBurst5m) || 1.4,
          microImpulseRangeExpansionTR: parseFloat(settings.microImpulseRangeExpansionTR) || 1.3,
          microImpulseSrBlockAtrMult: parseFloat(settings.microImpulseSrBlockAtrMult) || 0.6,
          microImpulseAtrMin1mPercent: parseFloat(settings.microImpulseAtrMin1mPercent) || 0.08,
          fastToGreenTimeoutEnabled: settings.fastToGreenTimeoutEnabled === "Yes" || settings.fastToGreenTimeoutEnabled === true,
          fastToGreenMaxSeconds5x: parseInt(settings.fastToGreenMaxSeconds5x) || 90,
          fastToGreenMaxSeconds10x: parseInt(settings.fastToGreenMaxSeconds10x) || 60,
          fastToGreenMaxSeconds20x: parseInt(settings.fastToGreenMaxSeconds20x) || 45,
          fastToGreenMinProgressFeeMult: parseFloat(settings.fastToGreenMinProgressFeeMult) || 0.8,
          fastToGreenExitSlippageBufferPct: parseFloat(settings.fastToGreenExitSlippageBufferPct) || 0.05,
          mode1AntiChopStrictEnabled: settings.mode1AntiChopStrictEnabled === "Yes" || settings.mode1AntiChopStrictEnabled === true,
          mode1AntiChopAdxMin: parseFloat(settings.mode1AntiChopAdxMin) || 18,
          mode1AntiChopVolumeDryThreshold: parseFloat(settings.mode1AntiChopVolumeDryThreshold) || 0.40,
          limitPostOnlyPreferredEnabled: settings.limitPostOnlyPreferredEnabled === "Yes" || settings.limitPostOnlyPreferredEnabled === true,
          maxFillWaitSeconds: parseInt(settings.maxFillWaitSeconds) || 6,
          spreadGuardEnabled: settings.spreadGuardEnabled === "Yes" || settings.spreadGuardEnabled === true,
          maxAllowedSpreadPercent: parseFloat(settings.maxAllowedSpreadPercent) || 0.08,
          applyWinRateBoostOn5x: settings.applyWinRateBoostOn5x === "Yes" || settings.applyWinRateBoostOn5x === true,
          applyWinRateBoostOn10x: settings.applyWinRateBoostOn10x === "Yes" || settings.applyWinRateBoostOn10x === true,
          applyWinRateBoostOn20x: settings.applyWinRateBoostOn20x === "Yes" || settings.applyWinRateBoostOn20x === true,
          
          // v3.9.10: Smart Exit Enhancements
          intentAutoDetectEnabled: settings.intentAutoDetectEnabled !== false && settings.intentAutoDetectEnabled !== "No",
          adaptiveProfitTrailEnabled: settings.adaptiveProfitTrailEnabled !== false && settings.adaptiveProfitTrailEnabled !== "No",
          adaptiveProfitTrailATRMultiplier: parseFloat(settings.adaptiveProfitTrailATRMultiplier) || 0.5,
          trailingStopFloorEnabled: settings.trailingStopFloorEnabled !== false && settings.trailingStopFloorEnabled !== "No",
          multiTargetTPEnabled: settings.multiTargetTPEnabled !== false && settings.multiTargetTPEnabled !== "No",
          multiTargetTightenAfterR1: parseFloat(settings.multiTargetTightenAfterR1) || 0.7,
          zeroLockMeaningfulEnabled: settings.zeroLockMeaningfulEnabled !== false && settings.zeroLockMeaningfulEnabled !== "No",
          zeroLockMeaningfulRule: settings.zeroLockMeaningfulRule || "sustained",
          zeroLockSustainedTicks: parseInt(settings.zeroLockSustainedTicks) || 3,
          // v4.7.0: Two-Stage Zero-Lock + Rescue Win-Conversion Timebox
          zeroLockStage2Threshold: parseFloat(settings.zeroLockStage2Threshold) || 0.30,
          rescueWinConversionTimeoutMin: parseInt(settings.rescueWinConversionTimeoutMin) || 5,
          aiCloseGuardEnabled: settings.aiCloseGuardEnabled !== false && settings.aiCloseGuardEnabled !== "No",
          aiCloseGuardMinNetProfit: parseFloat(settings.aiCloseGuardMinNetProfit) ?? 0.0,
          aiCloseGuardMinTPFraction: parseFloat(settings.aiCloseGuardMinTPFraction) || 0.15,
          
          // v3.9.11: Experience Mode (ML / Experimentation)
          experienceModeEnabled: settings.experienceModeEnabled === true || settings.experienceModeEnabled === "Yes",
          experienceNoTradeWeekends: settings.experienceNoTradeWeekends !== false && settings.experienceNoTradeWeekends !== "No",
          experienceAutoSuggestEnabled: settings.experienceAutoSuggestEnabled !== false && settings.experienceAutoSuggestEnabled !== "No",
          experienceAutoApplyEnabled: settings.experienceAutoApplyEnabled === true || settings.experienceAutoApplyEnabled === "Yes",
          experienceMinTradesForDecision: parseInt(settings.experienceMinTradesForDecision) || 20,
          experienceHoldoutPercent: parseFloat(settings.experienceHoldoutPercent) || 0.20,
          experienceAutoApplyMaxDelta: parseFloat(settings.experienceAutoApplyMaxDelta) || 0.15,
          
          // Trading Hours toggle - false = 24/7 mode, true = restricted hours
          tradingHoursEnabled: settings.tradingHoursEnabled === "Yes" || settings.tradingHoursEnabled === true,
          
          // Smart Symbol settings
          smartSymbolEnabled: settings.smartSymbolEnabled === "Yes" || settings.smartSymbolEnabled === true || smartSymbolEnabled,
          symbolUniverse: settings.symbolUniverse || selectedUniverse || "major_crypto",
          
          // Golden Hours Mode settings
          goldenHoursMode: settings.goldenHoursMode === "Yes" || settings.goldenHoursMode === true ? "Yes" : "No",
          goldenRepeatInterval: settings.goldenRepeatInterval || "5m",
          goldenRepeatCount: parseInt(settings.goldenRepeatCount) || 10,
          // Golden hour times per day (in user's timezone, will be converted to UTC by backend)
          goldenHourMon: settings.goldenHourMon || "",
          goldenHourTue: settings.goldenHourTue || "",
          goldenHourWed: settings.goldenHourWed || "",
          goldenHourThu: settings.goldenHourThu || "",
          goldenHourFri: settings.goldenHourFri || "",
          goldenHourSat: settings.goldenHourSat || "",
          goldenHourSun: settings.goldenHourSun || "",
          
          // Mode Preset metadata (v3.2.1)
          modeId: settings.modeId || selectedModeId || 'custom',
          modeName: settings.modeName || (BUILT_IN_MODES.find(m => m.id === selectedModeId)?.name) || 'Custom',
          modeSource: settings.modeSource || (selectedModeId !== 'custom' ? 'preset' : 'custom'),
          activeActions: settings.activeActions || selectedActions || [],
          
          // Pro Mode fields (v3.5.0)
          proMode: settings.proMode === true,
          proModeRollout: settings.proModeRollout || 'off',
          proModePendingPreference: settings.proModePendingPreference || 'normal',
          
          // Operational actions from setting actions
          ...(selectedActions.includes('daily_profit_cap') ? { dailyProfitCapPercent: 3, dailyProfitCapAction: 'pause' } : {}),
          ...(selectedActions.includes('daily_loss_cap') ? { dailyLossCapPercent: 2, dailyLossCapAction: 'pause' } : {}),
          ...(selectedActions.includes('buy_only') ? { sidePolicy: 'buy_only' } : {}),
          ...(selectedActions.includes('sell_only') ? { sidePolicy: 'sell_only' } : {}),
          ...(selectedActions.includes('sell_filter_tightening') ? { sellFilterStrict: true } : {}),
          ...(selectedActions.includes('low_rate_limit') ? { scanInterval: '15m', maxSignalsPerRun: '2' } : {}),
          ...(selectedActions.includes('max_concurrent_limit') ? { maxConcurrentPositions: Math.min(parseInt(settings.maxConcurrentPositions) || 3, 3) } : {}),
          
          // v4.5.3: Decision Model (was in UI but never saved to backend)
          decisionProfile: settings.decisionProfile || '',
          directionStackEnabled: settings.directionStackEnabled === true || settings.directionStackEnabled === "Yes",
          autoCloseAtTP1: settings.autoCloseAtTP1 === true || settings.autoCloseAtTP1 === "Yes",
          velocityCrashEnabled: settings.velocityCrashEnabled !== false && settings.velocityCrashEnabled !== "No",
          hardLossCapNet: parseFloat(settings.hardLossCapNet) || -1.3,
          
          // v4.4.10: Signal Matrix — Indicator Stack (persisted as-is)
          indicatorStackEnabled: settings.indicatorStack?.enabled === true || settings.indicatorStackEnabled === true || settings.indicatorStackEnabled === "Yes",
          indicatorStack: settings.indicatorStack || null,
          
          // v4.5.4: Scalp Gate + full pipeline settings
          scalpGateEnabled: settings.scalpGateEnabled === true || settings.scalpGateEnabled === "Yes",
          scalpGateProfile: settings.scalpGateProfile || 'balanced',
          scalpGateApplyOn5x: settings.scalpGateApplyOn5x === true || settings.scalpGateApplyOn5x === "Yes",
          scalpGateApplyOn10x: settings.scalpGateApplyOn10x === true || settings.scalpGateApplyOn10x === "Yes",
          scalpGateApplyOn20x: settings.scalpGateApplyOn20x === true || settings.scalpGateApplyOn20x === "Yes",
          disable30mTrendFilter: settings.disable30mTrendFilter === true || settings.disable30mTrendFilter === "Yes",
          disable5mConfirmationGate: settings.disable5mConfirmationGate === true || settings.disable5mConfirmationGate === "Yes",
          disableConsensusGate: settings.disableConsensusGate === true || settings.disableConsensusGate === "Yes",
          disableExhaustionGate: settings.disableExhaustionGate === true || settings.disableExhaustionGate === "Yes",
          disableImpulseGate: settings.disableImpulseGate === true || settings.disableImpulseGate === "Yes",
          disableEntryTimingNear15mHigh: settings.disableEntryTimingNear15mHigh === true || settings.disableEntryTimingNear15mHigh === "Yes",
          priceDriftGateMode: settings.priceDriftGateMode || 'strict',
          chopPolicy: settings.chopPolicy || 'block_all',
          widenEntryTimingPercent: parseFloat(settings.widenEntryTimingPercent) || 0,
          
          // v4.5.4: Side Policy — always send, quick actions may override below
          sidePolicy: settings.sidePolicy || 'both',
          
          // v4.5.4: Fast Green Scalp + full FGS settings
          fastGreenScalpEnabled: settings.fastGreenScalpEnabled === true || settings.fastGreenScalpEnabled === "Yes",
          fgsTrendOnlyPolicy: settings.fgsTrendOnlyPolicy !== false && settings.fgsTrendOnlyPolicy !== "No",
          fgsAllowRangeScalpsInNeutralHTF: settings.fgsAllowRangeScalpsInNeutralHTF === true || settings.fgsAllowRangeScalpsInNeutralHTF === "Yes",
          fastGreenTimeoutSec: parseInt(settings.fastGreenTimeoutSec) || 90,
          scalpTimeboxMinutes: parseInt(settings.scalpTimeboxMinutes) || 8,
          partialTpPercent: parseInt(settings.partialTpPercent) || 80,
          fgsLossCapBaseSLGross: parseFloat(settings.fgsLossCapBaseSLGross) || 0.35,
          fgsLossCapKSL: parseFloat(settings.fgsLossCapKSL) || 1.1,
          fgsHardMaxLossNet: parseFloat(settings.fgsHardMaxLossNet) || -1.30,
          fgsSymbolQualityFilterEnabled: settings.fgsSymbolQualityFilterEnabled === true || settings.fgsSymbolQualityFilterEnabled === "Yes",
          fgsSymbolBlacklistHours: parseInt(settings.fgsSymbolBlacklistHours) || 6,
          
          // Signal validity + net loss cap + first-tick safety
          signalValidityMinutes: parseInt(settings.signalValidityMinutes) || 30,
          netLossCapEnabled: settings.netLossCapEnabled === true || settings.netLossCapEnabled === "Yes",
          netLossCapPercent: parseFloat(settings.netLossCapPercent) || 0,
          firstTickExtraCapPct: parseFloat(settings.firstTickExtraCapPct) || 0.20,
          
          runImmediately: true, // Run on next cron tick instead of waiting for interval
        };
        
        console.log("[SCHEDULER] Saving schedule with autoExecute:", scheduleData.autoExecute, 
          "goldenHoursMode:", scheduleData.goldenHoursMode,
          scheduleData.goldenHoursMode === "Yes" ? `repeatInterval: ${scheduleData.goldenRepeatInterval}, repeatCount: ${scheduleData.goldenRepeatCount}` : `tradingHours (UTC): ${scheduleData.tradingHoursStart} - ${scheduleData.tradingHoursEnd}`);
        await StrategyEngineService.scheduleStrategy(strategyId, scheduleData);
        console.log("[SCHEDULER] Schedule saved successfully for strategy:", strategyId, "- will run on next cron tick");
      } catch (error) {
        console.error("[SCHEDULER] Error saving schedule:", error);
        setScheduleError(error.message || "Failed to save schedule");
      } finally {
        setScheduleSaving(false);
      }
    }

    onSettingsChange(updatedSettings);
  };

  if (!node) return null;

  return (
    <Paper sx={{ p: 3, maxWidth: 800, mx: "auto", mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        {node.data?.label || "Node"} Settings
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* AI Model Selection */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          AI Model
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="ai-model-label">Select AI Model</InputLabel>
          <Select
            labelId="ai-model-label"
            id="ai-model-select"
            value={selectedModel}
            onChange={handleModelChange}
            label="Select AI Model"
          >
            {AI_MODELS.map((model) => (
              <MenuItem key={model.id} value={model.id}>
                {model.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Assistant Mode - only for OpenAI models */}
        {AI_MODELS.find((m) => m.id === selectedModel)?.assistantSupport && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useAssistant}
                  onChange={handleAssistantToggle}
                  color="primary"
                />
              }
              label="Use OpenAI Assistant"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Assistants provide persistent memory and can use tools
            </Typography>

            {useAssistant && (
              <TextField
                fullWidth
                label="Assistant ID"
                value={assistantId}
                onChange={handleAssistantIdChange}
                margin="normal"
                helperText="Enter your OpenAI Assistant ID (leave empty to use default)"
              />
            )}
          </Box>
        )}

        {/* Connection Test */}
        <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={testStatus === "loading"}
            startIcon={
              testStatus === "loading" ? (
                <CircularProgress size={20} />
              ) : testStatus === "success" ? (
                <CheckCircleIcon color="success" />
              ) : testStatus === "error" ? (
                <ErrorIcon color="error" />
              ) : null
            }
          >
            Test Connection
          </Button>

          {testStatus && (
            <Typography
              variant="body2"
              sx={{ ml: 2 }}
              color={
                testStatus === "success"
                  ? "success.main"
                  : testStatus === "error"
                    ? "error.main"
                    : "text.secondary"
              }
            >
              {testMessage}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Prompt Configuration */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Custom Prompt</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Define the specific task for this {node.data?.label || "node"}
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={10}
            value={customPrompt}
            onChange={handlePromptChange}
            variant="outlined"
            placeholder={`Enter custom prompt for the ${node.data?.label || "node"}`}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleResetPrompt} variant="text">
              Reset to Default
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>Variable placeholders:</strong> You can use{" "}
            {"{parameter_name}"} to insert node parameters
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Broker/API Configuration - for nodes that need external connections */}
      {(node.type === "dataSource" ||
        node.type === "executionLayer" ||
        node.type === "technicalAnalyst") && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Broker & API Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 2 }}>
              {node.type === "dataSource" &&
                "Select the broker/exchange to fetch market data from."}
              {node.type === "executionLayer" &&
                "Select the broker/exchange to execute trades on."}
              {node.type === "technicalAnalyst" &&
                "Select the data source for technical indicators."}
            </Alert>

            <Grid container spacing={2}>
              {/* Broker Inheritance Status */}
              {node.data?.inheritedBroker && (
                <Grid item xs={12}>
                  <Alert
                    severity="warning"
                    icon={<LinkIcon sx={{ fontSize: 16 }} />}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() =>
                          setSettings({
                            ...settings,
                            broker: node.data.inheritedBroker,
                            useInheritedBroker: false,
                          })
                        }
                      >
                        Override
                      </Button>
                    }
                  >
                    <strong>Broker inherited from upstream:</strong>{" "}
                    {node.data.inheritedBroker}
                    {node.data.inheritedBrokerType === "strategy" &&
                      " (Strategy-wide)"}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>
                    {node.data?.inheritedBroker
                      ? "Broker/Exchange (Override)"
                      : "Broker/Exchange"}
                  </InputLabel>
                  <Select
                    value={
                      settings.broker || node.data?.inheritedBroker || "binance"
                    }
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        broker: e.target.value,
                        useInheritedBroker: false,
                      })
                    }
                    label={
                      node.data?.inheritedBroker
                        ? "Broker/Exchange (Override)"
                        : "Broker/Exchange"
                    }
                  >
                    {node.data?.inheritedBroker && !settings.broker && (
                      <MenuItem value={node.data.inheritedBroker}>
                        <LinkIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} /> {node.data.inheritedBroker} (Inherited)
                      </MenuItem>
                    )}
                    {AVAILABLE_BROKERS.map((broker) => (
                      <MenuItem key={broker.id} value={broker.id}>
                        {broker.type === "crypto" ? <CurrencyBitcoinIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} /> : <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />} {broker.name} (
                        {broker.fees})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* KuCoin Trading Type Selector */}
              {(settings.broker === "kucoin" || node.data?.inheritedBroker === "kucoin") && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>KuCoin Trading Type</InputLabel>
                      <Select
                        value={settings.kucoinTradeType || "spot"}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            kucoinTradeType: e.target.value,
                            // Reset leverage to 1 when switching to spot
                            leverage: e.target.value === "spot" ? "1" : settings.leverage || "1",
                          })
                        }
                        label="KuCoin Trading Type"
                      >
                        <MenuItem value="spot"><BarChartIcon sx={{ fontSize: 18, mr: 0.5 }} /> Spot Trading</MenuItem>
                        <MenuItem value="margin"><TrendingUpIcon sx={{ fontSize: 18, mr: 0.5 }} /> Margin Trading</MenuItem>
                        <MenuItem value="futures"><RocketLaunchIcon sx={{ fontSize: 18, mr: 0.5 }} /> Futures Trading</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Leverage Setting - Only for Margin and Futures */}
                  {(settings.kucoinTradeType === "margin" || settings.kucoinTradeType === "futures") && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Leverage</InputLabel>
                        <Select
                          value={settings.leverage || "1"}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              leverage: e.target.value,
                            })
                          }
                          label="Leverage"
                        >
                          <MenuItem value="1">1x (No Leverage)</MenuItem>
                          <MenuItem value="2">2x</MenuItem>
                          <MenuItem value="3">3x</MenuItem>
                          <MenuItem value="5">5x</MenuItem>
                          <MenuItem value="10">10x</MenuItem>
                          {settings.kucoinTradeType === "futures" && (
                            <>
                              <MenuItem value="20">20x</MenuItem>
                              <MenuItem value="50">50x</MenuItem>
                              <MenuItem value="75">75x</MenuItem>
                              <MenuItem value="100">100x</MenuItem>
                            </>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  
                  {/* Leverage Warning */}
                  {settings.kucoinTradeType === "futures" && parseInt(settings.leverage || "1") >= 10 && (
                    <Grid item xs={12}>
                      <Alert severity="warning" sx={{ py: 0.5 }}>
                        <Typography variant="caption">
                          <strong>High Leverage Warning:</strong> {settings.leverage}x leverage significantly increases risk. 
                          A {(100 / parseInt(settings.leverage)).toFixed(1)}% move against your position will liquidate it.
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                </>
              )}

              {node.type === "executionLayer" && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Trading Mode</InputLabel>
                    <Select
                      value={settings.paperTrading || "Yes"}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          paperTrading: e.target.value,
                        })
                      }
                      label="Trading Mode"
                    >
                      <MenuItem value="Yes"><DescriptionIcon sx={{ fontSize: 18, mr: 0.5 }} /> Paper Trading (Safe)</MenuItem>
                      <MenuItem value="No">
                        <AccountBalanceWalletIcon sx={{ fontSize: 18, mr: 0.5 }} /> Live Trading (Real Money)
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {(node.type === "dataSource" ||
                node.type === "technicalAnalyst") && (
                <>
                  {/* Symbol Inheritance Status */}
                  {node.data?.inheritedSymbol && (
                    <Grid item xs={12}>
                      <Alert
                        severity="info"
                        icon={<LinkIcon sx={{ fontSize: 16 }} />}
                        action={
                          <Button
                            color="inherit"
                            size="small"
                            onClick={() =>
                              setSettings({
                                ...settings,
                                symbol: node.data.inheritedSymbol,
                                useInheritedSymbol: false,
                              })
                            }
                          >
                            Override
                          </Button>
                        }
                      >
                        <strong>Symbol inherited from upstream:</strong>{" "}
                        {node.data.inheritedSymbol}
                        {node.data.inheritedType === "smart" &&
                          " (Smart Symbol Discovery)"}
                      </Alert>
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={
                        node.data?.inheritedSymbol
                          ? "Symbol (Override)"
                          : "Symbol"
                      }
                      value={
                        settings.symbol ||
                        node.data?.inheritedSymbol ||
                        "BTC/USDT"
                      }
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          symbol: e.target.value,
                          useInheritedSymbol: false,
                        })
                      }
                      placeholder="BTC/USDT"
                      helperText={
                        node.data?.inheritedSymbol
                          ? "Leave empty to use inherited symbol"
                          : "Trading pair symbol"
                      }
                      InputProps={{
                        startAdornment:
                          node.data?.inheritedSymbol && !settings.symbol ? (
                            <Chip
                              size="small"
                              label={<><LinkIcon sx={{ fontSize: 14, mr: 0.5 }} />Inherited</>}
                              sx={{ mr: 1 }}
                            />
                          ) : null,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Timeframe</InputLabel>
                      <Select
                        value={settings.timeframe || "1h"}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            timeframe: e.target.value,
                          })
                        }
                        label="Timeframe"
                      >
                        <MenuItem value="1m">1 Minute</MenuItem>
                        <MenuItem value="5m">5 Minutes</MenuItem>
                        <MenuItem value="15m">15 Minutes</MenuItem>
                        <MenuItem value="1h">1 Hour</MenuItem>
                        <MenuItem value="4h">4 Hours</MenuItem>
                        <MenuItem value="1d">1 Day</MenuItem>
                        <MenuItem value="1w">1 Week</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>

            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "background.default",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>API Status:</strong> Connected APIs are configured in{" "}
                <a
                  href="/settings/api-connections"
                  style={{ color: "#3b82f6" }}
                >
                  Settings → API Connections
                </a>
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Node-specific parameters */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Node Parameters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {node.data?.defaultParams &&
              Object.entries(node.data.defaultParams)
                .filter(
                  ([key]) =>
                    !["broker", "symbol", "timeframe", "paperTrading"].includes(
                      key,
                    ),
                )
                .map(([key, value]) => (
                  <Grid item xs={12} md={6} key={key}>
                    <TextField
                      fullWidth
                      label={
                        key.charAt(0).toUpperCase() +
                        key.slice(1).replace(/([A-Z])/g, " $1")
                      }
                      value={settings[key] || value}
                      onChange={(e) =>
                        setSettings({ ...settings, [key]: e.target.value })
                      }
                      variant="outlined"
                    />
                  </Grid>
                ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Smart Symbol Discovery - Phase 21 */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon color="primary" />
            <Typography variant="h6">Smart Symbol Discovery</Typography>
            {smartSymbolEnabled && (
              <Chip label="Enabled" size="small" color="success" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            Smart Symbol Discovery uses AI to automatically select the best
            trading symbol and broker based on market conditions, fees, and
            signal strength.
          </Alert>

          <FormControlLabel
            control={
              <Switch
                checked={smartSymbolEnabled}
                onChange={(e) => setSmartSymbolEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Enable Smart Symbol Discovery"
            sx={{ mb: 2 }}
          />

          {smartSymbolEnabled && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Symbol Universe</InputLabel>
                    <Select
                      value={selectedUniverse}
                      onChange={(e) => setSelectedUniverse(e.target.value)}
                      label="Symbol Universe"
                    >
                      {SYMBOL_UNIVERSES.map((universe) => (
                        <MenuItem key={universe.id} value={universe.id}>
                          {universe.name} ({universe.symbols.length} symbols)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Brokers</InputLabel>
                    <Select
                      multiple
                      value={selectedBrokers}
                      onChange={(e) => setSelectedBrokers(e.target.value)}
                      label="Brokers"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {AVAILABLE_BROKERS.map((broker) => (
                        <MenuItem key={broker.id} value={broker.id}>
                          {broker.name} (
                          {broker.type === "crypto" ? <CurrencyBitcoinIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} /> : <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />} {broker.fees}
                          )
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {selectedUniverse === "custom" && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Custom Symbols (comma-separated)"
                      value={customSymbols}
                      onChange={(e) => setCustomSymbols(e.target.value)}
                      placeholder="BTC/USDT, ETH/USDT, SOL/USDT"
                      helperText="Enter symbols separated by commas"
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography gutterBottom>
                    Minimum Confidence Threshold: {minConfidenceThreshold}%
                  </Typography>
                  <Slider
                    value={minConfidenceThreshold}
                    onChange={(e, value) => setMinConfidenceThreshold(value)}
                    min={30}
                    max={90}
                    step={5}
                    marks={[
                      { value: 30, label: "30%" },
                      { value: 60, label: "60%" },
                      { value: 90, label: "90%" },
                    ]}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={feeAwareSelection}
                        onChange={(e) => setFeeAwareSelection(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Tooltip title="Only select symbols where expected profit exceeds trading fees">
                        <span>Fee-Aware Selection (Recommended)</span>
                      </Tooltip>
                    }
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={
                    smartSymbolLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  onClick={handleTestSmartSymbol}
                  disabled={smartSymbolLoading}
                >
                  Test Discovery
                </Button>

                {smartSymbolResult && !smartSymbolResult.error && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="body2">
                      {smartSymbolResult.symbol ? (
                        <>
                          Recommended:{" "}
                          <strong>{smartSymbolResult.symbol}</strong> on{" "}
                          <strong>{smartSymbolResult.broker}</strong> (
                          {smartSymbolResult.direction},{" "}
                          {(smartSymbolResult.confidence * 100).toFixed(0)}%
                          confidence)
                        </>
                      ) : (
                        <>No trade signal found: {smartSymbolResult.reason}</>
                      )}
                    </Typography>
                  </Box>
                )}

                {smartSymbolResult?.error && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ErrorIcon color="error" />
                    <Typography variant="body2" color="error">
                      {smartSymbolResult.error}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: "background.default",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  <SecurityIcon
                    fontSize="small"
                    sx={{ verticalAlign: "middle", mr: 0.5 }}
                  />
                  Safety Guards Active:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Bounded search (max 10 symbols per pass)
                  <br />
                  • Anti-loop detection prevents oscillation
                  <br />
                  • Fee-aware profitability checks
                  <br />• Multi-broker comparison for best execution
                </Typography>
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Social Media APIs - Only for Fundamental Analyst nodes */}
      {node.type === "fundamentalAnalyst" && (
        <Accordion defaultExpanded sx={{ mt: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Social Media Data Sources</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enable social media data sources for sentiment analysis and news
              monitoring
            </Typography>

            <Grid container spacing={2}>
              {SOCIAL_MEDIA_APIS.map((api) => (
                <Grid item xs={12} md={6} key={api.id}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      {getSocialMediaIcon(api.id)}
                      <Typography
                        variant="subtitle1"
                        sx={{ ml: 1, flexGrow: 1 }}
                      >
                        {api.name}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={socialMediaConnections[api.id] || false}
                            onChange={(e) =>
                              handleToggleSocialMedia(api.id, e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={
                          socialMediaConnections[api.id] ? "Active" : "Inactive"
                        }
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {api.description}
                    </Typography>

                    {socialMediaConnections[api.id] && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mt: "auto",
                        }}
                      >
                        {apiTestStatus[api.id] === "loading" && (
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        {apiTestStatus[api.id] === "success" && (
                          <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                        )}
                        {apiTestStatus[api.id] === "error" && (
                          <ErrorIcon color="error" sx={{ mr: 1 }} />
                        )}
                        <Typography
                          variant="caption"
                          color={
                            apiTestStatus[api.id] === "success"
                              ? "success.main"
                              : apiTestStatus[api.id] === "error"
                                ? "error.main"
                                : "text.secondary"
                          }
                        >
                          {apiTestMessages[api.id] || ""}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Crypto Analytics APIs - Only for Fundamental Analyst nodes */}
      {node.type === "fundamentalAnalyst" && (
        <Accordion defaultExpanded sx={{ mt: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Crypto & Blockchain Analytics</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enable blockchain analytics platforms for on-chain data, whale
              alerts, and crypto market intelligence
            </Typography>

            <Grid container spacing={2}>
              {CRYPTO_ANALYTICS_APIS.map((api) => (
                <Grid item xs={12} md={6} key={api.id}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      {getCryptoAnalyticsIcon(api.id)}
                      <Typography
                        variant="subtitle1"
                        sx={{ ml: 1, flexGrow: 1 }}
                      >
                        {api.name}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              cryptoAnalyticsConnections[api.id] || false
                            }
                            onChange={(e) =>
                              handleToggleCryptoAnalytics(
                                api.id,
                                e.target.checked,
                              )
                            }
                            color="primary"
                          />
                        }
                        label={
                          cryptoAnalyticsConnections[api.id]
                            ? "Active"
                            : "Inactive"
                        }
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {api.description}
                    </Typography>

                    {cryptoAnalyticsConnections[api.id] && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mt: "auto",
                        }}
                      >
                        {apiTestStatus[`crypto_${api.id}`] === "loading" && (
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        {apiTestStatus[`crypto_${api.id}`] === "success" && (
                          <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                        )}
                        {apiTestStatus[`crypto_${api.id}`] === "error" && (
                          <ErrorIcon color="error" sx={{ mr: 1 }} />
                        )}

                        <Typography variant="caption" color="text.secondary">
                          {apiTestMessages[`crypto_${api.id}`] ||
                            "Click Test to verify connection"}
                        </Typography>

                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ ml: "auto" }}
                          onClick={() =>
                            handleTestApi("crypto_analytics", api.id)
                          }
                        >
                          Test
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Market Data APIs - Only for Fundamental Analyst nodes */}
      {node.type === "fundamentalAnalyst" && (
        <Accordion defaultExpanded sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Market Data Sources</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enable market data sources for fundamentals, financials, and news
            </Typography>

            <Grid container spacing={2}>
              {MARKET_DATA_APIS.map((api) => (
                <Grid item xs={12} md={6} key={api.id}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      {getMarketDataIcon(api.id)}
                      <Typography
                        variant="subtitle1"
                        sx={{ ml: 1, flexGrow: 1 }}
                      >
                        {api.name}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={marketDataConnections[api.id] || false}
                            onChange={(e) =>
                              handleToggleMarketData(api.id, e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label={
                          marketDataConnections[api.id] ? "Active" : "Inactive"
                        }
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {api.description}
                    </Typography>

                    {marketDataConnections[api.id] && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mt: "auto",
                        }}
                      >
                        {apiTestStatus[api.id] === "loading" && (
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        {apiTestStatus[api.id] === "success" && (
                          <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                        )}
                        {apiTestStatus[api.id] === "error" && (
                          <ErrorIcon color="error" sx={{ mr: 1 }} />
                        )}
                        <Typography
                          variant="caption"
                          color={
                            apiTestStatus[api.id] === "success"
                              ? "success.main"
                              : apiTestStatus[api.id] === "error"
                                ? "error.main"
                                : "text.secondary"
                          }
                        >
                          {apiTestMessages[api.id] || ""}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Scheduler Settings (v3.9.16 redesign) */}
      {node.type === "scheduler" && (
        <Box sx={{ mt: 2 }}>
          {/* Level 1: Mode Preset Selector */}
          <ModePresetSelector
            settings={settings}
            setSettings={setSettings}
            selectedModeId={selectedModeId}
            setSelectedModeId={setSelectedModeId}
            selectedActions={selectedActions}
            setSelectedActions={setSelectedActions}
            modeAppliedBanner={modeAppliedBanner}
            setModeAppliedBanner={setModeAppliedBanner}
          />

          {/* Level 2: Quick Controls */}
          <QuickControls
            settings={settings}
            setSettings={setSettings}
            handleSettingChange={handleSettingChange}
            node={node}
            brokerConnections={brokerConnections}
            brokerConnectionsLoading={brokerConnectionsLoading}
            nextExecutionInfo={nextExecutionInfo}
            countdown={countdown}
          />

          {/* Level 3: Advanced Accordions */}
          <AdvancedAccordions
            settings={settings}
            setSettings={setSettings}
            handleSettingChange={handleSettingChange}
          />
        </Box>
      )}


      {/* v4.9.0: Per-node settings for modular strategy nodes */}
      {node.type === "entryGate" && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon fontSize="small" sx={{ color: '#3b82f6' }} /> Entry Gate Settings
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Configure entry filters that must pass before any trade signal is accepted. Volume control, trend direction, candle confirmation, and more.
          </Alert>
          <AdvancedAccordions
            settings={settings}
            setSettings={setSettings}
            handleSettingChange={handleSettingChange}
            visibleSections={['entryFilters']}
          />
        </Box>
      )}

      {node.type === "exitEngine" && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TuneIcon fontSize="small" sx={{ color: '#F97316' }} /> Exit Engine Settings
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Configure exit logic: trailing TP/SL, zero-lock break-even, fixed TP ceiling, adaptive ATR trailing, and AI close guards.
          </Alert>
          <AdvancedAccordions
            settings={settings}
            setSettings={setSettings}
            handleSettingChange={handleSettingChange}
            visibleSections={['exitSystem', 'fixedTpCeiling', 'trailingZeroLock']}
          />
        </Box>
      )}

      {node.type === "rescueManager" && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HealthAndSafetyIcon fontSize="small" sx={{ color: '#EF4444' }} /> Rescue Manager Settings
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Configure rescue scale-in: trigger thresholds, premium sizing, AI feasibility gates, hard loss wall, cooldown, and exposure caps.
          </Alert>
          <AdvancedAccordions
            settings={settings}
            setSettings={setSettings}
            handleSettingChange={handleSettingChange}
            visibleSections={['rescue']}
          />
        </Box>
      )}

      {node.type === "budgetAllocator" && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceWalletIcon fontSize="small" sx={{ color: '#00FF94' }} /> Budget Allocator Settings
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Configure money management: strategy budget, slot allocation, leverage-aware fees, rescue reservation, and position sizing.
          </Alert>
          <AdvancedAccordions
            settings={settings}
            setSettings={setSettings}
            handleSettingChange={handleSettingChange}
            visibleSections={['moneyManagement']}
          />
        </Box>
      )}

      {node.type === "decisionEngine" && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToyIcon fontSize="small" sx={{ color: '#A855F7' }} /> Decision Engine Settings
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select a decision profile that auto-configures gate levels and exit settings. Profiles define P0/P1/P2 gate classification and confidence scoring.
          </Alert>
          <AdvancedAccordions
            settings={settings}
            setSettings={setSettings}
            handleSettingChange={handleSettingChange}
            visibleSections={['decisionModel']}
          />
        </Box>
      )}

      {/* AI Schedule Node Settings */}
      {node.type === "aiSchedule" && (
        <Accordion defaultExpanded sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon color="primary" fontSize="small" />
              <Typography variant="h6">AI Schedule Settings</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 2 }}>
              AI-managed scheduling with automatic mode selection, policy tuning, and daily optimization.
              When AI Mode is ON, the system dynamically selects the best trading mode and parameters based on market regime and performance.
            </Alert>

            <Grid container spacing={2}>
              {/* Master Toggles */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    <SmartToyIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} /> Core Toggles
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.aiModeEnabled === true || settings.aiModeEnabled === "Yes"}
                            onChange={(e) => handleSettingChange("aiModeEnabled", e.target.checked)}
                            color="primary"
                          />
                        }
                        label={<Typography variant="body2" fontWeight="bold">Enable AI Mode</Typography>}
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                        AI overrides mode & policy dynamically
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.experimentEnabled === true || settings.experimentEnabled === "Yes"}
                            onChange={(e) => handleSettingChange("experimentEnabled", e.target.checked)}
                            color="secondary"
                          />
                        }
                        label={<Typography variant="body2" fontWeight="bold">Experiment Mode</Typography>}
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                        Enables daily policy changes + data collection
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.tradeWeekends !== true && settings.tradeWeekends !== "Yes"}
                            onChange={(e) => handleSettingChange("tradeWeekends", !e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2" fontWeight="bold">No Weekend Trading</Typography>}
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                        Block entries on Sat & Sun
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Cadence & Rotation */}
              <Grid item xs={12} sm={6}>
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Daily Cadence</Typography>
                  <Chip label="Updates once per working day" size="small" icon={<AccessTimeIcon />} sx={{ mb: 1 }} />
                  <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <InputLabel>Rotation Policy</InputLabel>
                    <Select
                      value={settings.rotationPolicy || "round_robin"}
                      onChange={(e) => handleSettingChange("rotationPolicy", e.target.value)}
                      label="Rotation Policy"
                    >
                      <MenuItem value="round_robin">Round Robin (test all modes)</MenuItem>
                      <MenuItem value="bandit">Bandit (exploit best + explore)</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                    <InputLabel>Working Days</InputLabel>
                    <Select
                      value={settings.workingDays || "Mon,Tue,Wed,Thu,Fri"}
                      onChange={(e) => handleSettingChange("workingDays", e.target.value)}
                      label="Working Days"
                    >
                      <MenuItem value="Mon,Tue,Wed,Thu,Fri">Mon–Fri (default)</MenuItem>
                      <MenuItem value="Mon,Tue,Wed,Thu,Fri,Sat,Sun">All days</MenuItem>
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>

              {/* Safety Rails */}
              <Grid item xs={12} sm={6}>
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    <LockIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} /> Safety Rails (Hard Limits)
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth size="small"
                        label="Max Daily Loss %"
                        type="number"
                        value={settings.maxDailyLossPct ?? 3}
                        onChange={(e) => handleSettingChange("maxDailyLossPct", parseFloat(e.target.value))}
                        inputProps={{ min: 0.5, max: 10, step: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth size="small"
                        label="Max Drawdown %"
                        type="number"
                        value={settings.maxDrawdownPct ?? 10}
                        onChange={(e) => handleSettingChange("maxDrawdownPct", parseFloat(e.target.value))}
                        inputProps={{ min: 1, max: 25, step: 1 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth size="small"
                        label="Max Positions"
                        type="number"
                        value={settings.maxConcurrentPositions ?? 5}
                        onChange={(e) => handleSettingChange("maxConcurrentPositions", parseInt(e.target.value))}
                        inputProps={{ min: 1, max: 20, step: 1 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth size="small"
                        label="Max Spread %"
                        type="number"
                        value={settings.maxSpreadPct ?? 0.15}
                        onChange={(e) => handleSettingChange("maxSpreadPct", parseFloat(e.target.value))}
                        inputProps={{ min: 0.01, max: 1, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={<Switch checked={settings.correlationCapEnabled !== false} onChange={(e) => handleSettingChange("correlationCapEnabled", e.target.checked)} size="small" />}
                        label={<Typography variant="caption">Correlation Cap</Typography>}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={<Switch checked={settings.feeEdgeGuardEnabled !== false} onChange={(e) => handleSettingChange("feeEdgeGuardEnabled", e.target.checked)} size="small" />}
                        label={<Typography variant="caption">Fee-Edge Guard</Typography>}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Base Trading Settings */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Base Trading Settings
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
                    <Typography variant="caption">These settings define how the AI schedule executes trades. They are required for the strategy to open positions.</Typography>
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Broker</InputLabel>
                        <Select
                          value={settings.aiBroker || "kucoin"}
                          onChange={(e) => handleSettingChange("aiBroker", e.target.value)}
                          label="Broker"
                        >
                          <MenuItem value="kucoin">KuCoin</MenuItem>
                          <MenuItem value="binance">Binance</MenuItem>
                          <MenuItem value="bybit">Bybit</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Trading Mode</InputLabel>
                        <Select
                          value={settings.aiTradingMode || "futures"}
                          onChange={(e) => handleSettingChange("aiTradingMode", e.target.value)}
                          label="Trading Mode"
                        >
                          <MenuItem value="spot">Spot</MenuItem>
                          <MenuItem value="margin">Margin</MenuItem>
                          <MenuItem value="futures">Futures</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth size="small"
                        label="Leverage"
                        type="number"
                        value={settings.aiLeverage ?? 5}
                        onChange={(e) => handleSettingChange("aiLeverage", e.target.value)}
                        inputProps={{ min: 1, max: 20, step: 1 }}
                        disabled={settings.aiTradingMode === "spot"}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth size="small"
                        label="Position Size (USD)"
                        type="number"
                        value={settings.aiPositionSizeUsd ?? "100"}
                        onChange={(e) => handleSettingChange("aiPositionSizeUsd", e.target.value)}
                        inputProps={{ min: 10, step: 10 }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Symbol Universe</InputLabel>
                        <Select
                          value={settings.aiSymbolUniverse || "all_crypto"}
                          onChange={(e) => handleSettingChange("aiSymbolUniverse", e.target.value)}
                          label="Symbol Universe"
                        >
                          <MenuItem value="majors">Majors (10)</MenuItem>
                          <MenuItem value="top50">Top 50</MenuItem>
                          <MenuItem value="all_crypto">All Crypto (100+)</MenuItem>
                          <MenuItem value="defi">DeFi</MenuItem>
                          <MenuItem value="meme">Meme Coins</MenuItem>
                          <MenuItem value="ai_tokens">AI Tokens</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Scan Interval</InputLabel>
                        <Select
                          value={settings.aiScanInterval || "5m"}
                          onChange={(e) => handleSettingChange("aiScanInterval", e.target.value)}
                          label="Scan Interval"
                        >
                          <MenuItem value="1m">1 min</MenuItem>
                          <MenuItem value="2m">2 min</MenuItem>
                          <MenuItem value="5m">5 min</MenuItem>
                          <MenuItem value="10m">10 min</MenuItem>
                          <MenuItem value="15m">15 min</MenuItem>
                          <MenuItem value="30m">30 min</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth size="small"
                        label="Min Confidence %"
                        type="number"
                        value={settings.aiConfidenceThreshold ?? "82"}
                        onChange={(e) => handleSettingChange("aiConfidenceThreshold", e.target.value)}
                        inputProps={{ min: 50, max: 99, step: 1 }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Paper Trading</InputLabel>
                        <Select
                          value={settings.aiPaperTrading === true || settings.aiPaperTrading === "Yes" ? "Yes" : "No"}
                          onChange={(e) => handleSettingChange("aiPaperTrading", e.target.value)}
                          label="Paper Trading"
                        >
                          <MenuItem value="Yes">Yes (Simulation)</MenuItem>
                          <MenuItem value="No">No (Live)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Allowed Modes */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Allowed Modes (AI rotates through these)</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {[
                      { id: 'many_small_wins_scalp_5x', label: 'Scalp 5×' },
                      { id: 'many_small_wins_scalp_10x', label: 'Scalp 10×' },
                      { id: 'many_small_wins_scalp_20x', label: 'Scalp 20×' },
                      { id: 'fewer_larger_wins_hybrid_5x', label: 'Hybrid 5×' },
                      { id: 'fewer_larger_wins_trend_10x', label: 'Hybrid 10×' },
                      { id: 'pro_trader_brain_5x', label: 'Pro 5×' },
                      { id: 'pro_trader_brain_10x', label: 'Pro 10×' },
                      { id: 'pro_trader_brain_20x', label: 'Pro 20×' },
                    ].map(mode => {
                      const allowed = (settings.allowedModeIds || 'many_small_wins_scalp_5x,many_small_wins_scalp_10x,many_small_wins_scalp_20x,fewer_larger_wins_hybrid_5x,fewer_larger_wins_trend_10x,pro_trader_brain_5x,pro_trader_brain_10x,pro_trader_brain_20x').split(',');
                      const isActive = allowed.includes(mode.id);
                      return (
                        <Chip
                          key={mode.id}
                          label={mode.label}
                          size="small"
                          variant={isActive ? "filled" : "outlined"}
                          color={isActive ? "primary" : "default"}
                          onClick={() => {
                            let newList;
                            if (isActive) {
                              newList = allowed.filter(id => id !== mode.id);
                            } else {
                              newList = [...allowed, mode.id];
                            }
                            if (newList.length > 0) handleSettingChange("allowedModeIds", newList.join(','));
                          }}
                          sx={{ cursor: 'pointer', fontWeight: isActive ? 'bold' : 'normal' }}
                        />
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>

              {/* Status Display */}
              {node.data?.aiConfig && (
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'primary.main', borderRadius: 2, bgcolor: 'rgba(124,77,255,0.04)' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      <SmartToyIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom', color: 'primary.main' }} /> Current AI Status
                    </Typography>
                    <Grid container spacing={1}>
                      {node.data.aiConfig.currentPolicyPlan && (
                        <>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Mode</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {node.data.aiConfig.currentPolicyPlan.selectedModeId || '—'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Regime</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {node.data.aiConfig.currentPolicyPlan.regime?.label || '—'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Confidence</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {node.data.aiConfig.currentPolicyPlan.regime?.confidence
                                ? `${(node.data.aiConfig.currentPolicyPlan.regime.confidence * 100).toFixed(0)}%` : '—'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">Next Update</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {node.data.aiConfig.nextPolicyUpdateAt
                                ? new Date(node.data.aiConfig.nextPolicyUpdateAt).toLocaleTimeString() : '—'}
                            </Typography>
                          </Grid>
                        </>
                      )}
                      {!node.data.aiConfig.currentPolicyPlan && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            No policy plan generated yet. Enable Experiment Mode and save to start daily optimization.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button onClick={onClose} sx={{ mr: 2 }}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          Save Settings
        </Button>
      </Box>
    </Paper>
  );
}

export default NodeSettingsPanel;
